import React, { useCallback, useEffect, useMemo, useRef, useState, useDeferredValue } from 'react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import * as XLSX from 'xlsx';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './PendientesPages.css';
import { API_BASE_URL } from './config';
import { hasUserRole } from './userRoles';

ModuleRegistry.registerModules([AllCommunityModule]);

const COLUMN_VISIBILITY_STORAGE_KEY = 'facturacionColumnVisibility';
const COLUMN_WIDTHS_STORAGE_KEY = 'facturacionColumnWidths';
const FACTURAR_COLUMN_WIDTHS_STORAGE_KEY = 'facturarColumnWidths';
const EMPTY_PEDIDOS_SUMMARY = {
  totalPedidos: 0,
  totalDocumentos: 0,
  totalCosto: 0,
  totalPrecio: 0
};
const DEFAULT_PEDIDOS_PAGE_SIZE = 200;
const PEDIDOS_PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 250, 500, 1000];
const ADJUNTOS_MAX_PER_REGISTRO = 10;
const ADJUNTOS_ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.txt';


const FACTURACION_COLUMNS = [
  { headerName: '', field: '__select__', width: 48, checkboxSelection: true, headerCheckboxSelection: true, pinned: 'left', lockPinned: true, suppressMenu: true, sortable: false, filter: false, resizable: false },
  { headerName: 'Fecha', field: 'FECHA', minWidth: 160 },
  { headerName: 'Serie', field: 'SERIE', minWidth: 100 },
  { headerName: 'Folio', field: 'FOLIO', minWidth: 110 },
  { headerName: 'Razón social', field: 'RAZON_SOCIAL', minWidth: 200 },
  { headerName: 'Total', field: 'TOTAL', minWidth: 120 },
  { headerName: 'Referencia', field: 'REFERENCIA', minWidth: 140 },
  { headerName: 'Texto extra 1', field: 'TEXTO_EXTRA_1', minWidth: 150 },
  { headerName: 'Texto extra 2', field: 'TEXTO_EXTRA_2', minWidth: 150 },
  { headerName: 'Texto extra 3', field: 'TEXTO_EXTRA_3', minWidth: 150 },
  { headerName: 'Afectado', field: 'AFECTADO', minWidth: 110, editable: true },
  { headerName: 'Cancelado', field: 'CANCELADO', minWidth: 120 },
  { headerName: 'Total unidades', field: 'TOTAL_UNIDADES', minWidth: 140 },
  { headerName: 'Unidades pendientes', field: 'UNIDADES_PENDIENTES', minWidth: 170 },
  { headerName: 'Estatus', field: 'ESTATUS', minWidth: 150 }
];

const stripControlChars = (value) => {
  if (!value) return '';
  let cleaned = '';
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    const isControl = (code >= 0 && code <= 31) || code === 127;
    if (!isControl) {
      cleaned += value[index];
    }
  }
  return cleaned;
};

const normalizeHeaderKey = (key) => {
  if (key === null || typeof key === 'undefined') return '';
  const normalized = stripControlChars(
    key
      .toString()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  );
  return normalized
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_]/gi, '')
    .toUpperCase();
};

const buildHeaderLookup = () => {
  const mapping = {
    TOTAL_DE_UNIDADES: 'TOTAL_UNIDADES',
    TOTALUNIDADES: 'TOTAL_UNIDADES',
    UNIDADES_RESTANTES: 'UNIDADES_PENDIENTES',
    UNIDADES_RESUELTAS: 'UNIDADES_PENDIENTES',
    UNIDADES_PENDIENTE: 'UNIDADES_PENDIENTES'
  };
  FACTURACION_COLUMNS.forEach((col) => {
    if (!col.field || col.field === '__select__') return;
    mapping[normalizeHeaderKey(col.field)] = col.field;
    mapping[normalizeHeaderKey(col.headerName)] = col.field;
  });
  return mapping;
};

const EXCEL_HEADER_LOOKUP = buildHeaderLookup();

const PAGOS_COLUMNS = [
  { headerName: '', field: 'checked', checkboxSelection: true, headerCheckboxSelection: true, width: 40, pinned: 'left' },
  { headerName: 'SINIESTRO', field: 'SINIESTRO', flex: 1.2, minWidth: 150 },
  { headerName: 'Columna2', field: 'COLUMNA2', flex: 1, minWidth: 140 },
  { headerName: 'Columna3', field: 'COLUMNA3', flex: 1, minWidth: 140 },
  { headerName: 'Columna4', field: 'COLUMNA4', flex: 1, minWidth: 140 },
  { headerName: 'UUID', field: 'UUID', flex: 1, minWidth: 160 },
  { headerName: 'COSTO', field: 'COSTO', flex: 0.9, minWidth: 120 },
  { headerName: 'IVA', field: 'IVA', flex: 0.9, minWidth: 120 },
  { headerName: 'Columna8', field: 'COLUMNA8', flex: 1, minWidth: 140 },
  { headerName: 'Columna9', field: 'COLUMNA9', flex: 1, minWidth: 140 },
  { headerName: 'Columna10', field: 'COLUMNA10', flex: 1, minWidth: 140 },
  { headerName: 'TOTAL', field: 'TOTAL', flex: 0.9, minWidth: 140 },
  { headerName: 'Columna12', field: 'COLUMNA12', flex: 1, minWidth: 140 },
  { headerName: 'Columna13', field: 'COLUMNA13', flex: 1, minWidth: 140 },
  { headerName: 'ORDEN PEDIDO', field: 'ORDEN_PEDIDO', flex: 1.1, minWidth: 150 },
  { headerName: 'Columna15', field: 'COLUMNA15', flex: 1, minWidth: 140 },
  { headerName: 'FECHA', field: 'FECHA', flex: 1, minWidth: 160 }
];

const PAGOS_FIELD_LOOKUP = new Map(
  PAGOS_COLUMNS.map((column) => [normalizeHeaderKey(column.headerName || column.field), column.field])
);

const PAGOS_FIELD_ORDER = PAGOS_COLUMNS.map((column) => column.field);

const FACTURA_UPLOAD_HEADER_MAPPING = new Map([
  ['PEDIDO', 'pedido'],
  ['NUMERO_DE_PEDIDO', 'pedido'],
  ['NUMERO_PEDIDO', 'pedido'],
  ['NUM_PEDIDO', 'pedido'],
  ['NO_PEDIDO', 'pedido'],
  ['FOLIO_PEDIDO', 'pedido'],
  ['ORDEN_PEDIDO', 'pedido'],
  ['NUMERO_ORDEN', 'pedido'],
  ['NUMERODEPEDIDO', 'pedido'],
  ['NUMERO_PED', 'pedido'],
  ['PEDIDO_OC', 'pedido'],
  ['NUMERO_DE_FACTURA', 'numeroFactura'],
  ['NUMERO_FACTURA', 'numeroFactura'],
  ['NUM_FACTURA', 'numeroFactura'],
  ['FACTURA', 'numeroFactura'],
  ['FACTURA_NO', 'numeroFactura'],
  ['FOLIO_FACTURA', 'numeroFactura'],
  ['NO_FACTURA', 'numeroFactura'],
  ['N_FACTURA', 'numeroFactura'],
  ['NUMEROFACTURA', 'numeroFactura']
]);

const UUID_UPLOAD_HEADER_MAPPING = new Map([
  ['UUID', 'uuid'],
  ['FOLIO_FISCAL', 'uuid'],
  ['FOLIOFISCAL', 'uuid'],
  ['FOLIO_FISCAL_CFDI', 'uuid'],
  ['FOLIOFISCALCFDI', 'uuid'],
  ['CFDI_UUID', 'uuid'],
  ['UUID_CFDI', 'uuid'],
  ['UUID_FACTURA', 'uuid'],
  ['FOLIO_FISCAL_UUID', 'uuid'],
  ['FOLIOFISCALUUID', 'uuid']
]);

const toTrimmedExcelText = (value) => {
  if (value === null || typeof value === 'undefined') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value}`;
  }
  return String(value).trim();
};

const normalizeExcelPedidoValue = (value) => {
  const text = toTrimmedExcelText(value);
  if (!text) return '';
  return text.replace(/\.0+$/, '');
};

const normalizeExcelFacturaValue = (value) => {
  const text = toTrimmedExcelText(value);
  if (!text) return '';
  return text.replace(/\.0+$/, '');
};

const toNumber = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') return NaN;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  if (typeof value === 'string') {
    const normalized = value.replace(/[^0-9,.-]/g, '').replace(',', '.');
    if (!normalized.trim()) return NaN;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  }
  return NaN;
};

const formatCurrency = (value) => {
  const numeric = toNumber(value);
  if (Number.isNaN(numeric)) {
    return value ?? '';
  }
  return numeric.toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const numeroFacturaIndicatesFacturado = (value) => {
  const numeric = toNumber(value);
  return Number.isFinite(numeric) && numeric > 1;
};

const normalizePedidoValue = (value) => {
  if (value === null || typeof value === 'undefined') return '';
  return String(value).trim();
};

const normalizeBasePedido = (value) => {
  const normalized = normalizePedidoValue(value);
  if (!normalized) return '';
  return normalized.replace(/\.0+$/, '');
};

const formatISODateForDisplay = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  const paddedDay = day.padStart(2, '0');
  const paddedMonth = month.padStart(2, '0');
  return `${paddedDay}/${paddedMonth}/${year}`;
};

const PEDIDO_STATUS_LABELS = {
  facturar: 'Facturar',
  liberado: 'LIBERADO',
  facturado: 'Facturado',
  pagado: 'PAGADO',
  cancelado: 'Cancelado',
  'error en costo': 'ERROR EN COSTO',
  'no hay orden en sie': 'NO HAY ORDEN EN SIE',
  'pendiente de recepcion': 'Pendiente de recepción',
  default: 'Pendiente de recepción'
};

const PEDIDO_STATUS_STYLES = {
  facturar: { background: '#e5e7eb', color: '#111827', border: '#d4d4d8' },
  liberado: { background: '#e5e7eb', color: '#111827', border: '#d4d4d8' },
  facturado: { background: '#dcfce7', color: '#065f46', border: '#16a34a' },
  pagado: { background: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
  cancelado: { background: '#fee2e2', color: '#b91c1c', border: '#fecaca' },
  'error en costo': { background: '#ffedd5', color: '#9a3412', border: '#fed7aa' },
  'no hay orden en sie': { background: '#ede9fe', color: '#5b21b6', border: '#ddd6fe' },
  'pendiente de recepcion': { background: '#ccfbf1', color: '#0f766e', border: '#5eead4' },
  default: { background: '#f1f5f9', color: '#0f172a', border: '#e2e8f0' }
};

const normalizePedidoStatus = (value) => {
  if (value === null || typeof value === 'undefined') return 'pendiente de recepcion';
  const ascii = value
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^a-z\s]/g, '')
    .replace(/\s+/g, ' ');
  if (ascii === 'facturar') return 'facturar';
  if (ascii === 'liberado') return 'liberado';
  if (ascii === 'facturado') return 'facturado';
  if (ascii === 'pagado') return 'pagado';
  if (ascii === 'cancelado') return 'cancelado';
   if (ascii === 'error en costo') return 'error en costo';
  if (ascii === 'no hay orden en sie') return 'no hay orden en sie';
  if (ascii.startsWith('pendiente')) return 'pendiente de recepcion';
  if (ascii.includes('recepcion')) return 'pendiente de recepcion';
  return 'pendiente de recepcion';
};

const getPedidoStatusMeta = (status) => {
  const normalized = normalizePedidoStatus(status);
  const styles = PEDIDO_STATUS_STYLES[normalized] || PEDIDO_STATUS_STYLES.default;
  return {
    value: normalized,
    label: PEDIDO_STATUS_LABELS[normalized] || PEDIDO_STATUS_LABELS.default,
    styles
  };
};

const ASEGURADORA_FILTERS = [
  { value: 'all', label: 'TODOS', border: '#2563eb', activeBg: '#2563eb', activeColor: '#ffffff', textColor: '#2563eb' },
  { value: 'chubb', label: 'CHUBB', border: '#fca5a5', activeBg: '#fee2e2', activeColor: '#b91c1c', textColor: '#dc2626' },
  { value: 'multiasistencia', label: 'MULTIASISTENCIA', border: '#86efac', activeBg: '#dcfce7', activeColor: '#166534', textColor: '#15803d' },
  { value: 'mapfre', label: 'MAPFRE', border: '#c4b5fd', activeBg: '#ede9fe', activeColor: '#5b21b6', textColor: '#6d28d9' },
  { value: 'qualitas_xnuup', label: 'QUALITAS / XNUUP', border: '#fbbf24', activeBg: '#fef3c7', activeColor: '#92400e', textColor: '#a16207' },
  { value: 'vales_manuales', label: 'VALES MANUALES', border: '#0f172a', activeBg: '#e2e8f0', activeColor: '#0f172a', textColor: '#334155' }
];

const PEDIDO_STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'facturar', label: 'Solo facturar' },
  { value: 'facturado', label: 'Facturados' },
  { value: 'pagado', label: 'Pagados' },
  { value: 'error en costo', label: 'Error en costo' },
  { value: 'no hay orden en sie', label: 'No hay orden en SIE' },
  { value: 'pendiente de recepcion', label: 'Pendientes' },
  { value: 'cancelado', label: 'Cancelados' }
];
const FACTURACION_ESTATUS_PAGO_OPTIONS = ['', 'PAGADO', 'FACTURADO', 'FACTURAR', 'CANCELADO', 'REFACTURAR', 'ERROR EN COSTO', 'NO HAY ORDEN EN SIE'];
const FACTURADO_STATUS_VALUE = 'FACTURADO';
const FACTURADO_PEDIDO_STATUS = 'facturado';
const FACTURACION_ACTIVE_TAB_STORAGE_KEY = 'facturacionActiveTab';
const NUEVO_ESTATUS_EXCLUSION = ['CANCELADO', 'DEVUELTO', 'TRANSFERIDO'];
const FACTURAR_DETAIL_CELL_PADDING = '4px 6px';
const FACTURAR_DETAIL_ROW_HEIGHT = 32;
const FACTURAR_DETAIL_MAX_VISIBLE_ROWS = 8;
const FACTURAR_DETAIL_TABLE_HEADER_HEIGHT = 40;
const FACTURAR_DETAIL_TABLE_MAX_HEIGHT = FACTURAR_DETAIL_TABLE_HEADER_HEIGHT + (FACTURAR_DETAIL_ROW_HEIGHT * FACTURAR_DETAIL_MAX_VISIBLE_ROWS);
const FACTURAR_DETAIL_CELL_BASE_STYLE = {
  padding: FACTURAR_DETAIL_CELL_PADDING,
  height: FACTURAR_DETAIL_ROW_HEIGHT,
  boxSizing: 'border-box',
  lineHeight: `${Math.max(FACTURAR_DETAIL_ROW_HEIGHT - 12, 14)}px`
};

const facturarInputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '6px 8px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 13,
  fontWeight: 600,
  background: '#ffffff'
};

const facturarDisabledInputStyle = {
  ...facturarInputStyle,
  background: '#f8fafc',
  cursor: 'not-allowed'
};

const facturarFacturadoInputStyle = {
  ...facturarInputStyle,
  background: '#dcfce7',
  border: '1px solid #16a34a',
  color: '#065f46',
  fontWeight: 700
};

const facturarPagadoInputStyle = {
  ...facturarInputStyle,
  background: '#dbeafe',
  border: '1px solid #93c5fd',
  color: '#1e3a8a',
  fontWeight: 700
};

const facturarFacturarInputStyle = {
  ...facturarInputStyle,
  background: '#e5e7eb',
  border: '1px solid #d4d4d8',
  color: '#111827',
  fontWeight: 700
};

const facturarErrorCostoInputStyle = {
  ...facturarInputStyle,
  background: '#ffedd5',
  border: '1px solid #fed7aa',
  color: '#9a3412',
  fontWeight: 700
};

const facturarNoOrdenSieInputStyle = {
  ...facturarInputStyle,
  background: '#ede9fe',
  border: '1px solid #ddd6fe',
  color: '#5b21b6',
  fontWeight: 700
};

const facturarPendienteRecepcionInputStyle = {
  ...facturarInputStyle,
  background: '#ccfbf1',
  border: '1px solid #5eead4',
  color: '#0f766e',
  fontWeight: 700
};

const facturarCanceladoInputStyle = {
  ...facturarInputStyle,
  background: '#fee2e2',
  border: '1px solid #fecaca',
  color: '#b91c1c',
  fontWeight: 700
};

const stopGridPropagation = (event) => {
  if (!event) return;
  event.stopPropagation();
};

const getEffectivePedidoStatus = (pedidoStatus, estatusPago) => {
  const baseStatus = normalizePedidoStatus(pedidoStatus);
  const estatusPagoNormalized = (estatusPago || '')
    .toString()
    .trim()
    .toUpperCase();
  if (estatusPagoNormalized === 'PAGADO') return 'pagado';
  if (estatusPagoNormalized === 'ERROR EN COSTO') return 'error en costo';
  if (estatusPagoNormalized === 'NO HAY ORDEN EN SIE') return 'no hay orden en sie';
  return baseStatus;
};

const FacturarStatusRenderer = (props) => {
  if (props.data?.__rowType === 'detail') return null;
  const pedidoStatus = props.data?.pedidoStatus ?? props.value;
  const effectiveStatus = getEffectivePedidoStatus(pedidoStatus, props.data?.estatusPago);
  const meta = props.context?.getStatusMeta?.(effectiveStatus, props.data) || getPedidoStatusMeta(effectiveStatus);
  const styles = meta?.styles || { background: '#f1f5f9', color: '#0f172a', border: '#e2e8f0' };
  return (
    <span
      style={{
        ...facturarInputStyle,
        padding: '6px 8px',
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: styles.color,
        background: styles.background,
        border: `1px solid ${styles.border}`,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {meta?.label || 'Pendiente'}
    </span>
  );
};

const FacturarNumeroRenderer = (props) => {
  if (props.data?.__rowType === 'detail') return null;
  const pedidoKey = props.data?.key;
  const value = props.data?.numeroFactura || '';
  const context = props.context || {};
  const disabled = !pedidoKey || context?.isRowSaving?.(pedidoKey);
  const pedidoStatusNormalized = normalizePedidoStatus(props.data?.pedidoStatus);
  const estatusPagoNormalized = (props.data?.estatusPago || '')
    .toString()
    .trim()
    .toUpperCase();
  const inputStyle = (() => {
    if (disabled) return facturarDisabledInputStyle;
    if (estatusPagoNormalized === 'PAGADO') return facturarPagadoInputStyle;
    if (estatusPagoNormalized === 'ERROR EN COSTO') return facturarErrorCostoInputStyle;
    if (estatusPagoNormalized === 'NO HAY ORDEN EN SIE') return facturarNoOrdenSieInputStyle;
    if (estatusPagoNormalized === 'CANCELADO') return facturarCanceladoInputStyle;
    if (estatusPagoNormalized === FACTURADO_STATUS_VALUE) return facturarFacturadoInputStyle;
    if (pedidoStatusNormalized === 'facturar') return facturarFacturarInputStyle;
    if (pedidoStatusNormalized === 'pendiente de recepcion') return facturarPendienteRecepcionInputStyle;
    if (pedidoStatusNormalized === 'cancelado') return facturarCanceladoInputStyle;
    if (pedidoStatusNormalized === 'facturado') return facturarFacturadoInputStyle;
    return facturarInputStyle;
  })();
  return (
    <input
      type="text"
      value={value}
      placeholder="—"
      disabled={disabled}
      style={inputStyle}
      onClick={stopGridPropagation}
      onChange={(event) => {
        stopGridPropagation(event);
        context?.onNumeroFacturaChange?.(pedidoKey, event.target.value);
      }}
      onBlur={(event) => {
        stopGridPropagation(event);
        context?.onNumeroFacturaBlur?.(pedidoKey, event.target.value);
      }}
    />
  );
};

const FacturarEstatusPagoRenderer = (props) => {
  if (props.data?.__rowType === 'detail') return null;
  const pedidoKey = props.data?.key;
  const value = props.data?.estatusPago || '';
  const context = props.context || {};
  const disabled = !pedidoKey || context?.isRowSaving?.(pedidoKey);
  const pedidoStatusNormalized = normalizePedidoStatus(props.data?.pedidoStatus);
  const isFacturado = value.toString().trim().toUpperCase() === FACTURADO_STATUS_VALUE;
  const isPagado = value.toString().trim().toUpperCase() === 'PAGADO';
  const isErrorCosto = value.toString().trim().toUpperCase() === 'ERROR EN COSTO';
  const isNoOrdenSie = value.toString().trim().toUpperCase() === 'NO HAY ORDEN EN SIE';
  const isCancelado = value.toString().trim().toUpperCase() === 'CANCELADO';
  const appliedStyle = disabled
    ? facturarDisabledInputStyle
    : (isPagado
      ? facturarPagadoInputStyle
      : (isErrorCosto
        ? facturarErrorCostoInputStyle
        : (isNoOrdenSie
          ? facturarNoOrdenSieInputStyle
          : (isCancelado
            ? facturarCanceladoInputStyle
            : (isFacturado
              ? facturarFacturadoInputStyle
              : (pedidoStatusNormalized === 'facturar'
                ? facturarFacturarInputStyle
                : (pedidoStatusNormalized === 'pendiente de recepcion'
                  ? facturarPendienteRecepcionInputStyle
                  : (pedidoStatusNormalized === 'cancelado'
                    ? facturarCanceladoInputStyle
                    : (pedidoStatusNormalized === 'facturado'
                      ? facturarFacturadoInputStyle
                      : facturarInputStyle)))))))));
  return (
    <select
      value={value}
      disabled={disabled}
      style={appliedStyle}
      onClick={stopGridPropagation}
      onChange={(event) => {
        stopGridPropagation(event);
        context?.onEstatusPagoChange?.(pedidoKey, event.target.value);
      }}
    >
      {FACTURACION_ESTATUS_PAGO_OPTIONS.map((option) => (
        <option key={option || 'empty'} value={option}>{option || '—'}</option>
      ))}
    </select>
  );
};

const FacturarUuidRenderer = (props) => {
  if (props.data?.__rowType === 'detail') return null;
  const pedidoKey = props.data?.key;
  const value = props.data?.uuid || '';
  const context = props.context || {};
  const disabled = !pedidoKey || context?.isRowSaving?.(pedidoKey);
  const pedidoStatusNormalized = normalizePedidoStatus(props.data?.pedidoStatus);
  const estatusPagoNormalized = (props.data?.estatusPago || '')
    .toString()
    .trim()
    .toUpperCase();
  const appliedStyle = (() => {
    if (disabled) return facturarDisabledInputStyle;
    if (estatusPagoNormalized === 'PAGADO') return facturarPagadoInputStyle;
    if (estatusPagoNormalized === 'ERROR EN COSTO') return facturarErrorCostoInputStyle;
    if (estatusPagoNormalized === 'NO HAY ORDEN EN SIE') return facturarNoOrdenSieInputStyle;
    if (estatusPagoNormalized === 'CANCELADO') return facturarCanceladoInputStyle;
    if (estatusPagoNormalized === FACTURADO_STATUS_VALUE) return facturarFacturadoInputStyle;
    if (pedidoStatusNormalized === 'facturar') return facturarFacturarInputStyle;
    if (pedidoStatusNormalized === 'pendiente de recepcion') return facturarPendienteRecepcionInputStyle;
    if (pedidoStatusNormalized === 'cancelado') return facturarCanceladoInputStyle;
    if (pedidoStatusNormalized === 'facturado') return facturarFacturadoInputStyle;
    return facturarInputStyle;
  })();
  return (
    <input
      type="text"
      value={value}
      placeholder="—"
      disabled={disabled}
      style={{
        ...appliedStyle,
        fontFamily: 'monospace',
        fontSize: 12
      }}
      onClick={stopGridPropagation}
      onChange={(event) => {
        stopGridPropagation(event);
        context?.onUuidChange?.(pedidoKey, event.target.value);
      }}
      onBlur={(event) => {
        stopGridPropagation(event);
        context?.onUuidBlur?.(pedidoKey, event.target.value);
      }}
    />
  );
};

const FacturacionPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [excelPreview, setExcelPreview] = useState([]);
  const [facturasExcelData, setFacturasExcelData] = useState([]);
  const [facturasPuedeCargar, setFacturasPuedeCargar] = useState(false);
  const [facturasIsUploading, setFacturasIsUploading] = useState(false);
  const [uuidExcelData, setUuidExcelData] = useState([]);
  const [uuidPuedeCargar, setUuidPuedeCargar] = useState(false);
  const [uuidIsUploading, setUuidIsUploading] = useState(false);
  const [pagosData, setPagosData] = useState([]);
  const [searchPagosText, setSearchPagosText] = useState('');
  const [pagosExcelData, setPagosExcelData] = useState([]);
  const [pagosPuedeCargar, setPagosPuedeCargar] = useState(false);
  const [pagosIsUploading, setPagosIsUploading] = useState(false);
  const [pagosSelectedCount, setPagosSelectedCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [showColumnList, setShowColumnList] = useState(false);
  const [activeInnerTab, setActiveInnerTab] = useState(() => {
    if (typeof window === 'undefined') return 'facturar';
    const saved = localStorage.getItem(FACTURACION_ACTIVE_TAB_STORAGE_KEY);
    if (saved === 'pagos') return 'pagos';
    if (saved === 'listado') return 'listado';
    return 'facturar';
  });
  const [basePedidos, setBasePedidos] = useState([]);
  const [basePedidosSummary, setBasePedidosSummary] = useState(EMPTY_PEDIDOS_SUMMARY);
  const [basePedidosLoading, setBasePedidosLoading] = useState(false);
  const [basePedidosRefreshing, setBasePedidosRefreshing] = useState(false);
  const [basePedidosError, setBasePedidosError] = useState('');
  const [basePedidosPage, setBasePedidosPage] = useState(1);
  const [basePedidosPageSize, setBasePedidosPageSize] = useState(DEFAULT_PEDIDOS_PAGE_SIZE);
  const [basePedidosTotalPages, setBasePedidosTotalPages] = useState(1);
  const [basePedidosFilters, setBasePedidosFilters] = useState({ fechaInicio: '', fechaFin: '' });
  const [basePedidosFilterDraft, setBasePedidosFilterDraft] = useState({ fechaInicio: '', fechaFin: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [aseguradoraFilter, setAseguradoraFilter] = useState('all');
  const [pedidoFacturacionSaving, setPedidoFacturacionSaving] = useState({});
  const [expandedPedidoKey, setExpandedPedidoKey] = useState(null);
  const [pedidoDetailsCache, setPedidoDetailsCache] = useState({});
  const [pedidoDetailsLoading, setPedidoDetailsLoading] = useState({});
  const [pedidoDetailsErrors, setPedidoDetailsErrors] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnWidths, setColumnWidths] = useState({});
  const [columnPrefsLoaded, setColumnPrefsLoaded] = useState(false);
  const [facturarColumnWidths, setFacturarColumnWidths] = useState({});
  const [facturarColumnPrefsLoaded, setFacturarColumnPrefsLoaded] = useState(false);
  const [facturarSearchText, setFacturarSearchText] = useState('');
  const [facturarSelectedRows, setFacturarSelectedRows] = useState([]);
  const [facturacionAdjuntos, setFacturacionAdjuntos] = useState([]);
  const [facturacionAdjuntosLoading, setFacturacionAdjuntosLoading] = useState(false);
  const [facturacionAdjuntosError, setFacturacionAdjuntosError] = useState('');
  const [facturacionAdjuntosUploading, setFacturacionAdjuntosUploading] = useState(false);
  const [facturacionAdjuntosReloadKey, setFacturacionAdjuntosReloadKey] = useState(0);
  const gridRef = useRef(null);
  const facturarGridRef = useRef(null);
  const facturacionAdjuntosFileInputRef = useRef(null);
  const pagosGridRef = useRef(null);
  const selectionSyncRef = useRef(false);
  const fileInputRef = useRef(null);
  const facturasFileInputRef = useRef(null);
  const uuidFileInputRef = useRef(null);
  const pagosFileInputRef = useRef(null);
  const basePedidosFiltersRef = useRef(basePedidosFilters);
  const statusFilterRef = useRef(statusFilter);
  const aseguradoraFilterRef = useRef(aseguradoraFilter);
  const basePedidosInitializedRef = useRef(false);
  const facturarSearchRef = useRef('');
  const facturarSearchInitializedRef = useRef(false);
  const basePedidosPageSizeRef = useRef(DEFAULT_PEDIDOS_PAGE_SIZE);
  const deferredSearchText = useDeferredValue(searchText);
  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('usuario') || '{}');
    } catch (err) {
      return {};
    }
  }, []);
  const esSupervisor = hasUserRole(usuario, 'supervisor');

  const facturasUploadButtonStyle = useMemo(() => ({
    border: '1px solid #c7d2fe',
    borderRadius: 8,
    background: !facturasPuedeCargar || facturasIsUploading ? '#e5e7eb' : '#dbeafe',
    color: '#1d4ed8',
    fontWeight: 600,
    cursor: !facturasPuedeCargar || facturasIsUploading ? 'not-allowed' : 'pointer',
    opacity: facturasPuedeCargar ? 1 : 0.6
  }), [facturasPuedeCargar, facturasIsUploading]);

  const uuidUploadButtonStyle = useMemo(() => ({
    border: '1px solid #c7d2fe',
    borderRadius: 8,
    background: !uuidPuedeCargar || uuidIsUploading ? '#e5e7eb' : '#dbeafe',
    color: '#1d4ed8',
    fontWeight: 600,
    cursor: !uuidPuedeCargar || uuidIsUploading ? 'not-allowed' : 'pointer',
    opacity: uuidPuedeCargar ? 1 : 0.6
  }), [uuidPuedeCargar, uuidIsUploading]);

  const getRowIdentifier = useCallback((data) => {
    if (!data) return null;
    if (data.id != null) return String(data.id);
    if (data.FOLIO) return String(data.FOLIO);
    if (data.__uuid) return String(data.__uuid);
    return null;
  }, []);

  const loadFacturacion = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/facturacion/obtener`);
      if (!response.ok) {
        throw new Error('No se pudo obtener la información de facturación.');
      }
      const data = await response.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar facturación:', err);
      setErrorMessage(err.message || 'No se pudo cargar la información.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPagos = useCallback(() => {
    fetch(`${API_BASE_URL}/api/pagos/obtener`)
      .then((response) => response.json())
      .then((data) => {
        if (!Array.isArray(data)) {
          setPagosData([]);
          return;
        }
        setPagosData(data);
      })
      .catch(() => setPagosData([]));
  }, []);

  const loadBaseDatos = useCallback(async ({ page = 1, pageSize = DEFAULT_PEDIDOS_PAGE_SIZE, filters, status, aseguradora, search } = {}) => {
    const isInitialLoad = !basePedidosInitializedRef.current;
    if (isInitialLoad) {
      setBasePedidosLoading(true);
    } else {
      setBasePedidosRefreshing(true);
    }
    setBasePedidosError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(pageSize));
      const appliedFilters = filters || basePedidosFiltersRef.current || { fechaInicio: '', fechaFin: '' };
      if (appliedFilters.fechaInicio) {
        params.set('fechaInicio', appliedFilters.fechaInicio);
      }
      if (appliedFilters.fechaFin) {
        params.set('fechaFin', appliedFilters.fechaFin);
      }
      const resolvedStatus = typeof status === 'string' && status.length ? status : (statusFilterRef.current || 'all');
      if (resolvedStatus && resolvedStatus !== 'all') {
        params.set('status', resolvedStatus);
      }
      const resolvedAseguradora = typeof aseguradora === 'string' && aseguradora.length ? aseguradora : (aseguradoraFilterRef.current || 'all');
      if (resolvedAseguradora && resolvedAseguradora !== 'all') {
        params.set('aseguradora', resolvedAseguradora);
      }
      const normalizedSearch = typeof search === 'string' ? search.trim() : (facturarSearchRef.current || '');
      facturarSearchRef.current = normalizedSearch;
      if (normalizedSearch) {
        params.set('search', normalizedSearch);
      }
      const response = await fetch(`${API_BASE_URL}/api/basedatos/pedidos/resumen?${params.toString()}`);
      if (!response.ok) {
        throw new Error('No se pudo obtener la base de datos principal.');
      }
      const payload = await response.json();
      if (payload?.ok === false) {
        throw new Error(payload?.mensaje || 'No se pudo obtener la base de datos principal.');
      }
      const pedidos = Array.isArray(payload?.pedidos) ? payload.pedidos : [];
      const normalizedPedidos = pedidos.map((group) => ({
        key: group.key,
        pedido: group.pedido || 'Sin pedido registrado',
        aseguradora: group.aseguradora || '',
        totalRegistros: Number(group.totalRegistros) || 0,
        totalCosto: Number(group.totalCosto) || 0,
        totalPrecio: Number(group.totalPrecio) || 0,
        siniestro: group.siniestro || '',
        oc: group.oc || '',
        fechaPedido: group.fechaPedido || '',
        pedidoStatus: normalizePedidoStatus(group.pedidoEstatus),
        numeroFactura: normalizePedidoValue(group.numeroFactura) || '',
        estatusPago: normalizePedidoValue(group.estatusPago) || '',
        uuid: normalizePedidoValue(group.uuid) || ''
      }));
      const summary = payload?.summary && typeof payload.summary === 'object'
        ? payload.summary
        : EMPTY_PEDIDOS_SUMMARY;
      const pagination = payload?.pagination && typeof payload.pagination === 'object'
        ? payload.pagination
        : {};
      const resolvedPageSize = Number(pagination.pageSize) || pageSize;
      const resolvedPage = Number(pagination.page) || page;
      const resolvedTotalPages = Number(pagination.totalPages)
        || Math.max(1, Math.ceil((Number(summary?.totalPedidos) || 0) / Math.max(resolvedPageSize, 1)));
      setBasePedidos(normalizedPedidos);
      setBasePedidosSummary({
        totalPedidos: Number(summary.totalPedidos) || 0,
        totalDocumentos: Number(summary.totalDocumentos) || 0,
        totalCosto: Number(summary.totalCosto) || 0,
        totalPrecio: Number(summary.totalPrecio) || 0
      });
      setBasePedidosPage(Math.max(resolvedPage, 1));
      setBasePedidosPageSize(Math.max(resolvedPageSize, 1));
      setBasePedidosTotalPages(Math.max(resolvedTotalPages, 1));
      setExpandedPedidoKey(null);
      setPedidoDetailsCache({});
      setPedidoDetailsErrors({});
      setPedidoDetailsLoading({});
      const nextFiltersState = {
        fechaInicio: payload?.filters?.fechaInicio || '',
        fechaFin: payload?.filters?.fechaFin || ''
      };
      setBasePedidosFilters(nextFiltersState);
      if (typeof payload?.filters?.search === 'string') {
        facturarSearchRef.current = payload.filters.search.trim();
      }
      const appliedStatus = payload?.filters?.status || (resolvedStatus !== 'all' ? resolvedStatus : 'all');
      setStatusFilter(appliedStatus || 'all');
      statusFilterRef.current = appliedStatus || 'all';
      basePedidosInitializedRef.current = true;
    } catch (err) {
      console.error('Error al cargar base de datos principal:', err);
      setBasePedidosError(err.message || 'No se pudo cargar la base de datos principal.');
      if (!basePedidosInitializedRef.current) {
        setBasePedidos([]);
        setBasePedidosSummary({ ...EMPTY_PEDIDOS_SUMMARY });
        setBasePedidosPage(1);
        setBasePedidosTotalPages(1);
      }
    } finally {
      if (isInitialLoad) {
        setBasePedidosLoading(false);
      }
      setBasePedidosRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadFacturacion();
  }, [loadFacturacion]);

  useEffect(() => {
    loadPagos();
    const refreshPagos = () => loadPagos();
    window.addEventListener('refreshPagos', refreshPagos);
    return () => window.removeEventListener('refreshPagos', refreshPagos);
  }, [loadPagos]);

  useEffect(() => {
    loadBaseDatos({ page: 1, pageSize: DEFAULT_PEDIDOS_PAGE_SIZE });
  }, [loadBaseDatos]);

  useEffect(() => {
    basePedidosFiltersRef.current = basePedidosFilters;
  }, [basePedidosFilters]);

  useEffect(() => {
    setBasePedidosFilterDraft(basePedidosFilters);
  }, [basePedidosFilters]);

  useEffect(() => {
    localStorage.setItem(FACTURACION_ACTIVE_TAB_STORAGE_KEY, activeInnerTab);
  }, [activeInnerTab]);

  useEffect(() => {
    statusFilterRef.current = statusFilter;
  }, [statusFilter]);

  useEffect(() => {
    basePedidosPageSizeRef.current = basePedidosPageSize;
  }, [basePedidosPageSize]);

  useEffect(() => {
    const normalizedSearch = facturarSearchText.trim();
    const previousSearch = facturarSearchRef.current;
    facturarSearchRef.current = normalizedSearch;
    if (!facturarSearchInitializedRef.current) {
      facturarSearchInitializedRef.current = true;
      return;
    }
    if (previousSearch === normalizedSearch) {
      return;
    }
    const debounceId = setTimeout(() => {
      loadBaseDatos({ page: 1, pageSize: basePedidosPageSizeRef.current, search: normalizedSearch });
    }, 400);
    return () => clearTimeout(debounceId);
  }, [facturarSearchText, loadBaseDatos]);

  useEffect(() => {
    try {
      localStorage.setItem(FACTURACION_ACTIVE_TAB_STORAGE_KEY, activeInnerTab);
    } catch (err) {
      console.warn('No se pudo guardar la pestaña activa de facturación:', err);
    }
  }, [activeInnerTab]);

  const loadPedidoDetails = useCallback(async (pedidoKey) => {
    if (!pedidoKey) return;
    setPedidoDetailsLoading((prev) => ({ ...prev, [pedidoKey]: true }));
    setPedidoDetailsErrors((prev) => ({ ...prev, [pedidoKey]: '' }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/basedatos/pedidos/${encodeURIComponent(pedidoKey)}/filas`);
      if (!response.ok) {
        throw new Error('No se pudo obtener el detalle del pedido.');
      }
      const payload = await response.json();
      if (payload?.ok === false) {
        throw new Error(payload?.mensaje || 'No se pudo obtener el detalle del pedido.');
      }
      const rows = Array.isArray(payload?.rows) ? payload.rows : [];
      setPedidoDetailsCache((prev) => ({ ...prev, [pedidoKey]: { rows } }));
    } catch (err) {
      console.error('Error al cargar el detalle del pedido:', err);
      setPedidoDetailsErrors((prev) => ({ ...prev, [pedidoKey]: err.message || 'No se pudo obtener el detalle del pedido.' }));
    } finally {
      setPedidoDetailsLoading((prev) => {
        const next = { ...prev };
        delete next[pedidoKey];
        return next;
      });
    }
  }, []);

  useEffect(() => {
    try {
      const savedVisibility = JSON.parse(localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY) || '{}');
      setColumnVisibility(savedVisibility && typeof savedVisibility === 'object' ? savedVisibility : {});
    } catch (err) {
      console.warn('No se pudo leer la visibilidad de columnas de facturación:', err);
    }
    try {
      const savedWidths = JSON.parse(localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY) || '{}');
      setColumnWidths(savedWidths && typeof savedWidths === 'object' ? savedWidths : {});
    } catch (err) {
      console.warn('No se pudieron leer los anchos de columnas de facturación:', err);
    } finally {
      setColumnPrefsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!columnPrefsLoaded) return;
    localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility, columnPrefsLoaded]);

  useEffect(() => {
    if (!columnPrefsLoaded) return;
    localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(columnWidths));
  }, [columnWidths, columnPrefsLoaded]);

  useEffect(() => {
    try {
      const savedWidths = JSON.parse(localStorage.getItem(FACTURAR_COLUMN_WIDTHS_STORAGE_KEY) || '{}');
      setFacturarColumnWidths(savedWidths && typeof savedWidths === 'object' ? savedWidths : {});
    } catch (err) {
      console.warn('No se pudieron leer los anchos de columnas de Facturar:', err);
      setFacturarColumnWidths({});
    } finally {
      setFacturarColumnPrefsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!facturarColumnPrefsLoaded) return;
    localStorage.setItem(FACTURAR_COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(facturarColumnWidths));
  }, [facturarColumnWidths, facturarColumnPrefsLoaded]);

  useEffect(() => {
    if (!columnPrefsLoaded) return;
    const columnApi = gridRef.current?.columnApi;
    if (!columnApi?.setColumnVisible) return;
    FACTURACION_COLUMNS.forEach((column) => {
      const field = column.field;
      if (!field || field === '__select__') return;
      const visible = columnVisibility[field];
      columnApi.setColumnVisible(field, visible !== false);
    });
  }, [columnVisibility, columnPrefsLoaded]);

  const filteredRows = useMemo(() => {
    if (!deferredSearchText.trim()) return rows;
    const query = deferredSearchText.trim().toLowerCase();
    return rows.filter((row) =>
      FACTURACION_COLUMNS.filter(column => column.field !== '__select__').some((column) => {
        const value = row?.[column.field];
        if (value === null || typeof value === 'undefined') return false;
        return value.toString().toLowerCase().includes(query);
      })
    );
  }, [rows, deferredSearchText]);

  const filteredPagos = useMemo(() => {
    if (!searchPagosText) return pagosData;
    const query = searchPagosText.toLowerCase();
    return pagosData.filter((row) =>
      PAGOS_FIELD_ORDER.some((field) => {
        const value = row?.[field];
        if (value === null || typeof value === 'undefined') return false;
        return String(value).toLowerCase().includes(query);
      })
    );
  }, [pagosData, searchPagosText]);

  const totalImporte = useMemo(() => (
    filteredRows.reduce((acc, row) => {
      const numeric = toNumber(row?.TOTAL);
      if (Number.isNaN(numeric)) return acc;
      return acc + numeric;
    }, 0)
  ), [filteredRows]);

  const totalPendientes = useMemo(() => (
    filteredRows.reduce((acc, row) => {
      const numeric = toNumber(row?.UNIDADES_PENDIENTES);
      if (Number.isNaN(numeric)) return acc;
      return acc + numeric;
    }, 0)
  ), [filteredRows]);

  const totalUnidades = useMemo(() => (
    filteredRows.reduce((acc, row) => {
      const numeric = toNumber(row?.TOTAL_UNIDADES);
      if (Number.isNaN(numeric)) return acc;
      return acc + numeric;
    }, 0)
  ), [filteredRows]);

  const baseGroupedPedidos = useMemo(() => {
    if (!statusFilter || statusFilter === 'all') return basePedidos;
    return basePedidos.filter((group) => {
      const effectiveStatus = getEffectivePedidoStatus(group?.pedidoStatus, group?.estatusPago);
      if (statusFilter === 'facturar') {
        return effectiveStatus === 'facturar' || effectiveStatus === 'liberado';
      }
      return effectiveStatus === statusFilter;
    });
  }, [basePedidos, statusFilter]);
  const totalPedidosCount = basePedidosSummary.totalPedidos || 0;
  const facturarSearchInput = facturarSearchText.trim();
  const facturarSearchQuery = facturarSearchInput.toLowerCase();
  const facturarSearchActive = Boolean(facturarSearchInput);
  const facturarEmptyStateMessage = facturarSearchActive
    ? `No se encontraron coincidencias para "${facturarSearchInput}". Intenta otro pedido, siniestro u OC.`
    : 'No hay pedidos disponibles en la base principal.';
  const facturarFilteredPedidos = useMemo(() => {
    if (!facturarSearchQuery) return baseGroupedPedidos;
    return baseGroupedPedidos.filter((group) => {
      if (!group) return false;
      const candidates = [
        group.pedido,
        group.siniestro,
        group.oc,
        group.numeroFactura,
        group.pedidoStatus
      ];
      return candidates.some((value) => (
        value && value.toString().toLowerCase().includes(facturarSearchQuery)
      ));
    });
  }, [baseGroupedPedidos, facturarSearchQuery]);
  const facturarSelectionCount = facturarSelectedRows.length;
  const facturarHasSelection = facturarSelectionCount > 0;
  useEffect(() => {
    if (!facturarFilteredPedidos.length && facturarHasSelection) {
      setFacturarSelectedRows([]);
    }
  }, [facturarFilteredPedidos.length, facturarHasSelection]);
  const expandedPedidoGroup = useMemo(() => {
    if (!expandedPedidoKey) return null;
    return baseGroupedPedidos.find((group) => group.key === expandedPedidoKey) || null;
  }, [baseGroupedPedidos, expandedPedidoKey]);
  const expandedPedidoRows = expandedPedidoGroup ? (pedidoDetailsCache[expandedPedidoGroup.key]?.rows || []) : [];
  const expandedPedidoLoading = expandedPedidoGroup ? Boolean(pedidoDetailsLoading[expandedPedidoGroup.key]) : false;
  const expandedPedidoError = expandedPedidoGroup ? (pedidoDetailsErrors[expandedPedidoGroup.key] || '') : '';
  const expandedPedidoSiniestro = expandedPedidoGroup?.siniestro || '';
  const expandedPedidoOcLabel = expandedPedidoGroup ? (normalizePedidoValue(expandedPedidoGroup.oc) || 'Sin OC registrada') : '';
  const expandedPedidoTotals = useMemo(() => {
    if (!expandedPedidoGroup) return null;
    const subtotal = toNumber(expandedPedidoGroup.totalPrecio);
    if (Number.isNaN(subtotal)) {
      return {
        subtotalFormatted: '—',
        ivaFormatted: '—',
        totalFormatted: '—'
      };
    }
    const iva = subtotal * 0.16;
    const total = subtotal * 1.16;
    return {
      subtotalFormatted: formatCurrency(subtotal),
      ivaFormatted: formatCurrency(iva),
      totalFormatted: formatCurrency(total)
    };
  }, [expandedPedidoGroup]);
    const detailOverlayState = (() => {
      if (!expandedPedidoGroup) {
        return { type: 'empty', message: 'Selecciona un pedido en la tabla inferior y pulsa “Ver filas” para mostrar sus registros.' };
      }
      if (expandedPedidoLoading) {
        return { type: 'loading', message: 'Cargando filas de este pedido...' };
      }
      if (expandedPedidoError) {
        return { type: 'error', message: expandedPedidoError };
      }
      if (!expandedPedidoRows.length) {
        return { type: 'empty-result', message: 'No se encontraron filas para este pedido.' };
      }
      return null;
    })();
  const expandedPedidoNeedsScroll = expandedPedidoRows.length > FACTURAR_DETAIL_MAX_VISIBLE_ROWS;
  const expandedPedidoFillerRows = expandedPedidoGroup && expandedPedidoRows.length < FACTURAR_DETAIL_MAX_VISIBLE_ROWS
    ? FACTURAR_DETAIL_MAX_VISIBLE_ROWS - expandedPedidoRows.length
    : 0;
  const basePedidosRangeStart = baseGroupedPedidos.length ? ((basePedidosPage - 1) * basePedidosPageSize) + 1 : 0;
  const basePedidosRangeEnd = baseGroupedPedidos.length
    ? Math.min(basePedidosRangeStart + baseGroupedPedidos.length - 1, totalPedidosCount || basePedidosRangeStart + baseGroupedPedidos.length - 1)
    : 0;
  const canGoPrevPedidosPage = basePedidosPage > 1;
  const canGoNextPedidosPage = basePedidosPage < basePedidosTotalPages;
  const statusFilterActive = statusFilter !== 'all';
  const statusFilterLabel = statusFilterActive ? (PEDIDO_STATUS_LABELS[statusFilter] || 'filtrado') : '';
  const basePedidosRangeLabel = (() => {
    if (!totalPedidosCount) {
      return 'No hay pedidos disponibles en la base principal.';
    }
    if (!baseGroupedPedidos.length) {
      if (statusFilterActive) {
        return `No hay pedidos con estatus ${statusFilterLabel} en esta página.`;
      }
      return 'No hay pedidos en esta página.';
    }
    const suffix = statusFilterActive ? ` (estatus: ${statusFilterLabel})` : '';
    return `Mostrando ${basePedidosRangeStart.toLocaleString('es-MX')} - ${basePedidosRangeEnd.toLocaleString('es-MX')} de ${totalPedidosCount.toLocaleString('es-MX')} pedidos${suffix}`;
  })();

  const hasActiveDateFilter = Boolean(basePedidosFilters.fechaInicio || basePedidosFilters.fechaFin);
  const dateFilterSummaryLabel = useMemo(() => {
    if (!hasActiveDateFilter) return '';
    if (basePedidosFilters.fechaInicio && basePedidosFilters.fechaFin) {
      return `Filtrando del ${formatISODateForDisplay(basePedidosFilters.fechaInicio)} al ${formatISODateForDisplay(basePedidosFilters.fechaFin)}`;
    }
    if (basePedidosFilters.fechaInicio) {
      return `Filtrando desde ${formatISODateForDisplay(basePedidosFilters.fechaInicio)}`;
    }
    return `Filtrando hasta ${formatISODateForDisplay(basePedidosFilters.fechaFin)}`;
  }, [basePedidosFilters, hasActiveDateFilter]);
  const isDateRangeInvalid = Boolean(
    basePedidosFilterDraft.fechaInicio &&
    basePedidosFilterDraft.fechaFin &&
    basePedidosFilterDraft.fechaInicio > basePedidosFilterDraft.fechaFin
  );
  const showClearDateFilterButton = Boolean(
    hasActiveDateFilter || basePedidosFilterDraft.fechaInicio || basePedidosFilterDraft.fechaFin
  );
  const isBasePedidosBusy = basePedidosLoading || basePedidosRefreshing;
  const dateFilterApplyDisabled = isBasePedidosBusy || isDateRangeInvalid;

  const handleTogglePedidoRows = useCallback((pedidoKey, options = {}) => {
    if (!pedidoKey) return;
    const { forceClose = false } = options;
    setExpandedPedidoKey((current) => {
      if (forceClose) {
        return current === pedidoKey ? null : current;
      }
      if (current === pedidoKey) {
        return current;
      }
      if (!pedidoDetailsCache[pedidoKey]) {
        loadPedidoDetails(pedidoKey);
      }
      return pedidoKey;
    });
  }, [loadPedidoDetails, pedidoDetailsCache]);

  const handleRetryPedidoDetails = useCallback(() => {
    if (!expandedPedidoGroup?.key) return;
    loadPedidoDetails(expandedPedidoGroup.key);
  }, [expandedPedidoGroup, loadPedidoDetails]);

  const handlePedidosPrevPage = useCallback(() => {
    if (basePedidosLoading || basePedidosRefreshing || basePedidosPage <= 1) return;
    loadBaseDatos({ page: basePedidosPage - 1, pageSize: basePedidosPageSize });
  }, [basePedidosLoading, basePedidosRefreshing, basePedidosPage, basePedidosPageSize, loadBaseDatos]);

  const handlePedidosNextPage = useCallback(() => {
    if (basePedidosLoading || basePedidosRefreshing || basePedidosPage >= basePedidosTotalPages) return;
    loadBaseDatos({ page: basePedidosPage + 1, pageSize: basePedidosPageSize });
  }, [basePedidosLoading, basePedidosRefreshing, basePedidosPage, basePedidosPageSize, basePedidosTotalPages, loadBaseDatos]);

  const handlePedidosPageSizeChange = useCallback((event) => {
    const value = Number(event?.target?.value);
    if (!Number.isFinite(value) || value <= 0) return;
    loadBaseDatos({ page: 1, pageSize: value });
  }, [loadBaseDatos]);

  const handleDateFilterInputChange = useCallback((field, value) => {
    setBasePedidosFilterDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleApplyPedidoDateFilter = useCallback(() => {
    if (isDateRangeInvalid) return;
    const nextFilters = {
      fechaInicio: basePedidosFilterDraft.fechaInicio || '',
      fechaFin: basePedidosFilterDraft.fechaFin || ''
    };
    setBasePedidosFilters(nextFilters);
    basePedidosFiltersRef.current = nextFilters;
    loadBaseDatos({ page: 1, pageSize: basePedidosPageSize, filters: nextFilters });
  }, [basePedidosFilterDraft, basePedidosPageSize, isDateRangeInvalid, loadBaseDatos]);

  const handleClearPedidoDateFilter = useCallback(() => {
    const draftHasValues = Boolean(basePedidosFilterDraft.fechaInicio || basePedidosFilterDraft.fechaFin);
    if (!hasActiveDateFilter && !draftHasValues) return;
    const nextFilters = { fechaInicio: '', fechaFin: '' };
    setBasePedidosFilterDraft(nextFilters);
    if (!hasActiveDateFilter) return;
    setBasePedidosFilters(nextFilters);
    basePedidosFiltersRef.current = nextFilters;
    loadBaseDatos({ page: 1, pageSize: basePedidosPageSize, filters: nextFilters });
  }, [basePedidosFilterDraft, basePedidosPageSize, hasActiveDateFilter, loadBaseDatos]);

  const handleStatusFilterChange = useCallback((value) => {
    if (value === statusFilterRef.current) return;
    setStatusFilter(value);
    statusFilterRef.current = value;
    const currentFilters = basePedidosFiltersRef.current || { fechaInicio: '', fechaFin: '' };
    loadBaseDatos({ page: 1, pageSize: basePedidosPageSize, filters: currentFilters, status: value });
  }, [basePedidosPageSize, loadBaseDatos]);

  const handleAseguradoraFilterChange = useCallback((value) => {
    if (value === aseguradoraFilterRef.current) return;
    setAseguradoraFilter(value);
    aseguradoraFilterRef.current = value;
    const currentFilters = basePedidosFiltersRef.current || { fechaInicio: '', fechaFin: '' };
    loadBaseDatos({
      page: 1,
      pageSize: basePedidosPageSize,
      filters: currentFilters,
      status: statusFilterRef.current || 'all',
      aseguradora: value
    });
  }, [basePedidosPageSize, loadBaseDatos]);

  const handleFacturarRowClicked = useCallback((event) => {
    if (!event?.data?.key) return;
    const nativeEvent = event.event;
    if (nativeEvent?.target && typeof nativeEvent.target.closest === 'function') {
      const interactive = nativeEvent.target.closest('input,select,textarea,button');
      if (interactive) {
        return;
      }
    }
    handleTogglePedidoRows(event.data.key);
  }, [handleTogglePedidoRows]);

  const handleFacturarSelectionChanged = useCallback(() => {
    const api = facturarGridRef.current?.api;
    if (!api) return;
    const selected = api.getSelectedRows() || [];
    setFacturarSelectedRows(selected);
  }, [facturarGridRef]);

  const handleExportFacturarSelection = useCallback(() => {
    const api = facturarGridRef.current?.api;
    if (!api) return;
    const selected = api.getSelectedRows() || [];
    if (!selected.length) {
      alert('Selecciona al menos un pedido para exportar.');
      return;
    }
    const rowsForExcel = selected.map((row) => {
      const subtotal = toNumber(row?.totalPrecio);
      const hasSubtotal = !Number.isNaN(subtotal);
      const ivaImporte = hasSubtotal ? Number((subtotal * 0.16).toFixed(2)) : '';
      const totalImporte = hasSubtotal ? Number((subtotal * 1.16).toFixed(2)) : '';
      const subtotalImporte = hasSubtotal ? Number(subtotal.toFixed(2)) : '';
      const statusMeta = getPedidoStatusMeta(row?.pedidoStatus);
      return {
        'Fecha de pedido': formatISODateForDisplay(row?.fechaPedido) || '',
        Pedido: row?.pedido || '',
        'Filas registradas': Number(row?.totalRegistros) || 0,
        Siniestro: row?.siniestro || '',
        'Orden de compra': row?.oc || '',
        'Estatus del pedido': statusMeta?.label || '',
        'Número de factura': row?.numeroFactura || '',
        'Estatus de pago': row?.estatusPago || '',
        UUID: row?.uuid || '',
        'Precio MXN': subtotalImporte,
        'IVA 16%': ivaImporte,
        Total: totalImporte
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(rowsForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pedidos');
    const isoDate = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `facturar_seleccion_${isoDate}.xlsx`);
  }, [facturarGridRef]);

  const updateGroupedPedidoField = useCallback((pedidoKey, field, value) => {
    if (!pedidoKey || !field) return;
    setBasePedidos((prev) => {
      if (!Array.isArray(prev) || !prev.length) return prev;
      let changed = false;
      const next = prev.map((group) => {
        if (!group || group.key !== pedidoKey) return group;
        changed = true;
        return { ...group, [field]: value };
      });
      return changed ? next : prev;
    });
  }, []);

  const persistPedidoFacturacionFields = useCallback(async (pedidoKey, updates) => {
    if (!pedidoKey || !updates || typeof updates !== 'object') return;
    const payload = {};
    ['numeroFactura', 'estatusPago', 'uuid'].forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(updates, field)) {
        payload[field] = updates[field];
      }
    });
    if (!Object.keys(payload).length) return;
    setPedidoFacturacionSaving((prev) => ({ ...prev, [pedidoKey]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/basedatos/pedidos/${encodeURIComponent(pedidoKey)}/facturacion`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.ok === false) {
        throw new Error(result?.mensaje || 'No se pudo actualizar la información de facturación.');
      }
    } catch (err) {
      console.error('Error al actualizar datos de facturación del pedido:', err);
      alert(err.message || 'No se pudo actualizar la información de facturación.');
    } finally {
      setPedidoFacturacionSaving((prev) => {
        const next = { ...prev };
        delete next[pedidoKey];
        return next;
      });
    }
  }, []);

  const handlePedidoNumeroFacturaChange = useCallback((pedidoKey, value) => {
    updateGroupedPedidoField(pedidoKey, 'numeroFactura', value);
    if (numeroFacturaIndicatesFacturado(value)) {
      updateGroupedPedidoField(pedidoKey, 'estatusPago', FACTURADO_STATUS_VALUE);
      updateGroupedPedidoField(pedidoKey, 'pedidoStatus', FACTURADO_PEDIDO_STATUS);
    } else if (!value || !value.trim()) {
      updateGroupedPedidoField(pedidoKey, 'estatusPago', '');
    }
  }, [updateGroupedPedidoField]);

  const handlePedidoNumeroFacturaBlur = useCallback((pedidoKey, value) => {
    const updates = { numeroFactura: value };
    if (numeroFacturaIndicatesFacturado(value)) {
      updates.estatusPago = FACTURADO_STATUS_VALUE;
      updates.pedidoStatus = FACTURADO_PEDIDO_STATUS;
    } else if (!value || !value.trim()) {
      updates.estatusPago = '';
    }
    persistPedidoFacturacionFields(pedidoKey, updates);
  }, [persistPedidoFacturacionFields]);

  const handlePedidoEstatusPagoChange = useCallback((pedidoKey, value) => {
    updateGroupedPedidoField(pedidoKey, 'estatusPago', value);

    const normalized = (value || '')
      .toString()
      .trim()
      .toUpperCase();

    if (normalized === 'ERROR EN COSTO') {
      updateGroupedPedidoField(pedidoKey, 'pedidoStatus', 'error en costo');
    }

    if (normalized === 'NO HAY ORDEN EN SIE') {
      updateGroupedPedidoField(pedidoKey, 'pedidoStatus', 'no hay orden en sie');
    }

    persistPedidoFacturacionFields(pedidoKey, { estatusPago: value });
  }, [persistPedidoFacturacionFields, updateGroupedPedidoField]);

  const handlePedidoUuidChange = useCallback((pedidoKey, value) => {
    updateGroupedPedidoField(pedidoKey, 'uuid', value);
  }, [updateGroupedPedidoField]);

  const handlePedidoUuidBlur = useCallback((pedidoKey, value) => {
    persistPedidoFacturacionFields(pedidoKey, { uuid: value });
  }, [persistPedidoFacturacionFields]);

  const reapplySelection = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    selectionSyncRef.current = true;
    const selectedSet = new Set(selectedRowIds);
    api.forEachNode((node) => {
      const rowKey = getRowIdentifier(node?.data);
      const shouldSelect = rowKey && selectedSet.has(rowKey);
      if (node.isSelected() !== shouldSelect) {
        node.setSelected(shouldSelect);
      }
    });
    selectionSyncRef.current = false;
  }, [selectedRowIds, getRowIdentifier]);

  useEffect(() => {
    reapplySelection();
  }, [rows, reapplySelection]);

  const handleSelectionChanged = useCallback(() => {
    if (selectionSyncRef.current) return;
    const api = gridRef.current?.api;
    if (!api) return;
    const selectedRows = api.getSelectedRows();
    const ids = Array.isArray(selectedRows)
      ? selectedRows.map((row) => getRowIdentifier(row)).filter(Boolean)
      : [];
    setSelectedRowIds(ids);
    setSelectedCount(ids.length);
  }, [getRowIdentifier]);

  const selectedFacturacionPedido = useMemo(() => {
    const firstRow = Array.isArray(facturarSelectedRows) && facturarSelectedRows.length
      ? facturarSelectedRows[0]
      : null;

    const rawPedido = firstRow?.PEDIDO ?? firstRow?.Pedido ?? firstRow?.pedido ?? '';
    const pedido = String(rawPedido || '').trim();

    return pedido || null;
  }, [facturarSelectedRows]);

  const facturacionAdjuntosSlotsDisponibles = selectedFacturacionPedido
    ? Math.max(ADJUNTOS_MAX_PER_REGISTRO - facturacionAdjuntos.length, 0)
    : 0;

  useEffect(() => {
    if (!selectedFacturacionPedido) {
      setFacturacionAdjuntos([]);
      setFacturacionAdjuntosError('');
      setFacturacionAdjuntosLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setFacturacionAdjuntosLoading(true);
    setFacturacionAdjuntosError('');

    fetch(`${API_BASE_URL}/api/basedatos/pedido/${encodeURIComponent(selectedFacturacionPedido)}/adjuntos`, { signal: controller.signal })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.mensaje || 'No se pudieron cargar los adjuntos.');
        }
        return payload;
      })
      .then((data) => {
        setFacturacionAdjuntos(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        console.error('No se pudieron cargar los adjuntos de Facturación:', err);
        setFacturacionAdjuntos([]);
        setFacturacionAdjuntosError(err.message || 'No se pudieron cargar los adjuntos.');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setFacturacionAdjuntosLoading(false);
        }
      });

    return () => controller.abort();
  }, [selectedFacturacionPedido, facturacionAdjuntosReloadKey]);

  const handleFacturacionAdjuntosReload = useCallback(() => {
    if (!selectedFacturacionPedido) return;
    setFacturacionAdjuntosReloadKey((value) => value + 1);
  }, [selectedFacturacionPedido]);

  const handleFacturacionAdjuntosUploadClick = useCallback(() => {
    if (!selectedFacturacionPedido) {
      alert('Selecciona un pedido en Facturación para agregar adjuntos.');
      return;
    }

    facturacionAdjuntosFileInputRef.current?.click();
  }, [selectedFacturacionPedido]);

  const handleFacturacionAdjuntosFileChange = useCallback(async (event) => {
    if (!selectedFacturacionPedido) return;

    const files = Array.from(event?.target?.files || []);
    event.target.value = '';

    if (!files.length) return;

    const disponibles = Math.max(ADJUNTOS_MAX_PER_REGISTRO - facturacionAdjuntos.length, 0);
    if (disponibles <= 0) {
      alert('Este registro ya tiene el máximo de adjuntos permitidos.');
      return;
    }

    const filesToUpload = files.slice(0, disponibles);

    if (files.length > disponibles) {
      alert(`Solo se subirán ${disponibles} archivo(s), porque el registro permite máximo ${ADJUNTOS_MAX_PER_REGISTRO}.`);
    }

    const formData = new FormData();
    filesToUpload.forEach((file) => formData.append('archivos', file));
    formData.append('subidoPor', usuario?.nombre || usuario?.name || usuario?.usuario || 'Facturación');

    setFacturacionAdjuntosUploading(true);
    setFacturacionAdjuntosError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/basedatos/pedido/${encodeURIComponent(selectedFacturacionPedido)}/adjuntos`, {
        method: 'POST',
        body: formData
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.mensaje || 'No se pudieron subir los adjuntos.');
      }

      const nuevos = Array.isArray(payload?.adjuntos) ? payload.adjuntos : [];

      if (nuevos.length) {
        setFacturacionAdjuntos((prev) => [...nuevos, ...prev]);
      } else {
        handleFacturacionAdjuntosReload();
      }
    } catch (err) {
      console.error('No se pudieron subir los adjuntos de Facturación:', err);
      alert(err.message || 'No se pudieron subir los adjuntos.');
    } finally {
      setFacturacionAdjuntosUploading(false);
    }
  }, [selectedFacturacionPedido, facturacionAdjuntos.length, usuario, handleFacturacionAdjuntosReload]);

  const handleFacturacionAdjuntoDelete = useCallback(async (adjunto) => {
    const numericId = Number(adjunto?.id);
    if (!Number.isFinite(numericId)) return;

    const confirmed = window.confirm('¿Deseas eliminar este archivo?');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/basedatos/adjuntos/${numericId}`, {
        method: 'DELETE'
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.mensaje || 'No se pudo eliminar el adjunto.');
      }

      setFacturacionAdjuntos((prev) => prev.filter((item) => Number(item?.id) !== numericId));
    } catch (err) {
      console.error('No se pudo eliminar el adjunto de Facturación:', err);
      alert(err.message || 'No se pudo eliminar el adjunto.');
    }
  }, []);

  const hasFacturacionRegistroSeleccionado = Boolean(selectedFacturacionPedido);

  useEffect(() => {
    if (!expandedPedidoKey) return;
    const exists = facturarFilteredPedidos.some((group) => group?.key === expandedPedidoKey);
    if (!exists) {
      setExpandedPedidoKey(null);
    }
  }, [expandedPedidoKey, facturarFilteredPedidos]);

  const applyWidthState = useCallback((columnApi, widths) => {
    if (!columnApi?.applyColumnState || !widths) return;
    const state = Object.entries(widths)
      .filter(([colId, width]) => colId && colId !== '__select__' && typeof width === 'number' && width > 0)
      .map(([colId, width]) => ({ colId, width }));
    if (state.length) {
      columnApi.applyColumnState({ state, applyOrder: false });
    }
  }, []);

  const handleColumnResized = useCallback((event) => {
    if (!event?.column || !event?.finished) return;
    const colId = event.column.getColId();
    if (!colId || colId === '__select__') return;
    const width = event.column.getActualWidth();
    setColumnWidths((prev) => ({ ...prev, [colId]: width }));
    const columnApi = event.columnApi || gridRef.current?.columnApi;
    if (!columnApi?.getColumnState) return;
    const state = columnApi.getColumnState();
    if (!Array.isArray(state)) return;
    const widths = {};
    state.forEach(col => {
      if (!col?.colId || col.colId === '__select__') return;
      if (typeof col.width !== 'number' || Number.isNaN(col.width)) return;
      widths[col.colId] = col.width;
    });
    try {
      localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    } catch (err) {
      console.warn('No se pudieron guardar los anchos de las columnas:', err);
    }
  }, []);

  const handleFacturarColumnResized = useCallback((event) => {
    if (!event?.column || !event?.finished) return;
    const colId = event.column.getColId();
    if (!colId) return;
    const width = event.column.getActualWidth();
    setFacturarColumnWidths((prev) => ({ ...prev, [colId]: width }));
    const columnApi = event.columnApi || facturarGridRef.current?.columnApi;
    if (!columnApi?.getColumnState) return;
    const state = columnApi.getColumnState();
    if (!Array.isArray(state)) return;
    const widths = {};
    state.forEach((col) => {
      if (!col?.colId) return;
      if (typeof col.width !== 'number' || Number.isNaN(col.width)) return;
      widths[col.colId] = col.width;
    });
    try {
      localStorage.setItem(FACTURAR_COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    } catch (err) {
      console.warn('No se pudieron guardar los anchos de las columnas de Facturar:', err);
    }
  }, []);

  const handleToggleColumn = useCallback((field) => {
    const currentlyVisible = columnVisibility[field] !== false;
    const nextVisible = !currentlyVisible;
    setColumnVisibility((prev) => ({
      ...prev,
      [field]: nextVisible
    }));
    const columnApi = gridRef.current?.columnApi;
    if (columnApi?.setColumnVisible) {
      columnApi.setColumnVisible(field, nextVisible);
    }
  }, [columnVisibility]);

  const handleGridReady = useCallback((params) => {
    const columnApi = params?.columnApi;
    if (!columnApi) return;
    let visibilitySettings = columnVisibility;
    let widthSettings = columnWidths;
    if (!columnPrefsLoaded) {
      try {
        const storedVisibility = JSON.parse(localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY) || '{}');
        if (storedVisibility && typeof storedVisibility === 'object') {
          visibilitySettings = storedVisibility;
        }
      } catch (err) {
        console.warn('No se pudo leer la visibilidad guardada:', err);
      }
      try {
        const storedWidths = JSON.parse(localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY) || '{}');
        if (storedWidths && typeof storedWidths === 'object') {
          widthSettings = storedWidths;
        }
      } catch (err) {
        console.warn('No se pudieron leer los anchos guardados:', err);
      }
    }
    FACTURACION_COLUMNS.forEach((column) => {
      const field = column.field;
      if (!field || field === '__select__') return;
      const visible = visibilitySettings[field];
      columnApi.setColumnVisible(field, visible !== false);
    });
    applyWidthState(columnApi, widthSettings);
    if (!columnPrefsLoaded) {
      setColumnVisibility(visibilitySettings);
      setColumnWidths(widthSettings);
      setColumnPrefsLoaded(true);
    }
  }, [columnVisibility, columnWidths, columnPrefsLoaded, applyWidthState]);

  const handleFacturarGridReady = useCallback((params) => {
    const columnApi = params?.columnApi;
    if (!columnApi) return;
    applyWidthState(columnApi, facturarColumnWidths);
  }, [applyWidthState, facturarColumnWidths]);

  useEffect(() => {
    if (!columnPrefsLoaded) return;
    const columnApi = gridRef.current?.columnApi;
    applyWidthState(columnApi, columnWidths);
  }, [columnWidths, columnPrefsLoaded, applyWidthState]);

  useEffect(() => {
    if (!facturarColumnPrefsLoaded) return;
    const columnApi = facturarGridRef.current?.columnApi;
    applyWidthState(columnApi, facturarColumnWidths);
  }, [facturarColumnPrefsLoaded, facturarColumnWidths, applyWidthState]);

  const handleDeleteSelected = async () => {
    const api = gridRef.current?.api;
    if (!api || isDeleting) return;
    const selectedRows = api.getSelectedRows();
    const ids = selectedRows
      .map((row) => Number(row?.id))
      .filter((id) => Number.isInteger(id));
    if (!ids.length) {
      alert('Selecciona al menos un registro.');
      return;
    }
    const confirmed = window.confirm('¿Seguro que deseas eliminar los registros seleccionados? Esta acción no se puede deshacer.');
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/facturacion/borrar`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.ok === false) {
        throw new Error(result?.mensaje || 'No se pudieron borrar los registros seleccionados.');
      }
      await loadFacturacion();
      setSelectedRowIds((prev) => {
        const remaining = prev.filter((rowKey) => {
          const numericId = Number(rowKey);
          if (!Number.isNaN(numericId)) {
            return !ids.includes(numericId);
          }
          return true;
        });
        setSelectedCount(remaining.length);
        return remaining;
      });
    } catch (err) {
      console.error('Error al borrar facturación:', err);
      alert(err.message || 'No se pudieron borrar los registros seleccionados.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCellValueChanged = useCallback(async (params) => {
    if (params?.colDef?.field !== 'AFECTADO') return;
    const rowId = params?.data?.id;
    const newValue = params?.newValue ?? '';
    const oldValue = params?.oldValue ?? '';
    if (newValue === oldValue) return;
    const numericId = Number(rowId);
    if (!Number.isInteger(numericId)) {
      alert('No se pudo identificar el registro para actualizar.');
      params?.node?.setDataValue('AFECTADO', oldValue);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/facturacion/actualizar-afectado`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: numericId, afectado: newValue ?? '' })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.ok === false) {
        throw new Error(result?.mensaje || 'No se pudo actualizar el campo Afectado.');
      }
      setRows((prev) => (
        Array.isArray(prev)
          ? prev.map((row) => (row.id === numericId ? { ...row, AFECTADO: newValue ?? '' } : row))
          : prev
      ));
    } catch (err) {
      console.error('Error al actualizar Afectado:', err);
      params?.node?.setDataValue('AFECTADO', oldValue ?? '');
      alert(err.message || 'No se pudo actualizar el campo Afectado.');
    }
  }, [setRows]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const parsed = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        const normalizedRows = parsed
          .map((rawRow) => {
            const normalized = {};
            Object.entries(rawRow).forEach(([key, value]) => {
              const header = EXCEL_HEADER_LOOKUP[normalizeHeaderKey(key)];
              if (header) {
                normalized[header] = value ?? '';
              }
            });
            const hasContent = Object.values(normalized).some((value) => value !== '' && value !== null);
            return hasContent ? normalized : null;
          })
          .filter(Boolean);
        setExcelPreview(normalizedRows);
        setErrorMessage('');
      } catch (err) {
        console.error('Error al leer el archivo:', err);
        setErrorMessage('No se pudo leer el archivo seleccionado.');
        setExcelPreview([]);
      }
    };
    reader.readAsBinaryString(file);
    event.target.value = '';
  };

  const handleUploadExcel = async () => {
    if (!excelPreview.length || isUploading) {
      alert('Primero carga un archivo de Excel con información.');
      return;
    }
    const totalToUpload = excelPreview.length;
    setIsUploading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/facturacion/insertar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(excelPreview)
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.ok === false) {
        throw new Error(result?.mensaje || 'No se pudo cargar la información.');
      }
      setExcelPreview([]);
      await loadFacturacion();
      const inserted = Number(result?.inserted ?? 0);
      const updated = Number(result?.updated ?? 0);
      const skipped = Number(result?.skipped ?? Math.max(totalToUpload - inserted - updated, 0));
      alert(`Se cargaron ${inserted} registros nuevos, se actualizaron ${updated} y se omitieron ${skipped}.`);
    } catch (err) {
      console.error('Error al subir facturación:', err);
      alert(err.message || 'Ocurrió un error al subir la información.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFacturasFileUpload = useCallback((event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const binary = evt.target?.result;
      if (!binary) return;
      const workbook = XLSX.read(binary, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) return;
      const worksheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
      const dedupMap = new Map();
      rawRows.forEach((raw) => {
        if (!raw || typeof raw !== 'object') return;
        const normalized = {};
        Object.entries(raw).forEach(([key, value]) => {
          const mappedField = FACTURA_UPLOAD_HEADER_MAPPING.get(normalizeHeaderKey(key));
          if (mappedField && !Object.prototype.hasOwnProperty.call(normalized, mappedField)) {
            normalized[mappedField] = value;
          }
        });
        const pedido = normalizeExcelPedidoValue(normalized.pedido);
        const numeroFactura = normalizeExcelFacturaValue(normalized.numeroFactura);
        if (!pedido || !numeroFactura) return;
        dedupMap.set(pedido, numeroFactura);
      });
      const preparedRows = Array.from(dedupMap.entries()).map(([pedido, numeroFactura]) => ({ pedido, numeroFactura }));
      setFacturasExcelData(preparedRows);
      setFacturasPuedeCargar(preparedRows.length > 0);
      if (facturasFileInputRef.current) {
        facturasFileInputRef.current.value = '';
      }
    };
    reader.readAsBinaryString(file);
  }, []);

  const handleCargarFacturasExcel = useCallback(async () => {
    if (!facturasExcelData.length || facturasIsUploading) return;
    setFacturasIsUploading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/basedatos/facturas/actualizar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: facturasExcelData })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.mensaje || 'No se pudieron actualizar los numeros de factura.');
      }
      const updatedRows = Number(payload?.updated) || 0;
      const pedidosProcesados = Number(payload?.pedidos) || facturasExcelData.length;
      alert(`Se actualizaron ${updatedRows} renglones para ${pedidosProcesados} pedidos.`);
      setFacturasExcelData([]);
      setFacturasPuedeCargar(false);
      if (facturasFileInputRef.current) {
        facturasFileInputRef.current.value = '';
      }
      loadBaseDatos({ page: basePedidosPage, pageSize: basePedidosPageSize });
    } catch (err) {
      console.error('Error al actualizar numeros de factura:', err);
      alert(err.message || 'No se pudieron actualizar los numeros de factura.');
    } finally {
      setFacturasIsUploading(false);
    }
  }, [facturasExcelData, facturasIsUploading, loadBaseDatos, basePedidosPage, basePedidosPageSize]);

  const handleUuidFileUpload = useCallback((event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const binary = evt.target?.result;
      if (!binary) return;
      const workbook = XLSX.read(binary, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) return;
      const worksheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
      const dedupMap = new Map();
      rawRows.forEach((raw) => {
        if (!raw || typeof raw !== 'object') return;
        let numeroFacturaValue;
        let uuidValue;
        Object.entries(raw).forEach(([key, value]) => {
          const normalizedKey = normalizeHeaderKey(key);
          if (!numeroFacturaValue) {
            const mapped = FACTURA_UPLOAD_HEADER_MAPPING.get(normalizedKey);
            if (mapped === 'numeroFactura') {
              numeroFacturaValue = value;
            }
          }
          if (!uuidValue) {
            const mappedUuid = UUID_UPLOAD_HEADER_MAPPING.get(normalizedKey);
            if (mappedUuid === 'uuid') {
              uuidValue = value;
            }
          }
        });
        const numeroFactura = normalizeExcelFacturaValue(numeroFacturaValue);
        const uuid = toTrimmedExcelText(uuidValue);
        if (!numeroFactura || !uuid) return;
        dedupMap.set(numeroFactura.toUpperCase(), { numeroFactura, uuid });
      });
      const preparedRows = Array.from(dedupMap.values());
      setUuidExcelData(preparedRows);
      setUuidPuedeCargar(preparedRows.length > 0);
      if (uuidFileInputRef.current) {
        uuidFileInputRef.current.value = '';
      }
    };
    reader.readAsBinaryString(file);
  }, []);

  const handleCargarUuidExcel = useCallback(async () => {
    if (!uuidExcelData.length || uuidIsUploading) return;
    setUuidIsUploading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/basedatos/uuid/actualizar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: uuidExcelData })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.mensaje || 'No se pudieron actualizar los UUID.');
      }
      const updatedRows = Number(payload?.updated) || 0;
      const facturasProcesadas = Number(payload?.facturas) || uuidExcelData.length;
      alert(`Se actualizaron ${updatedRows} UUID para ${facturasProcesadas} facturas.`);
      setUuidExcelData([]);
      setUuidPuedeCargar(false);
      if (uuidFileInputRef.current) {
        uuidFileInputRef.current.value = '';
      }
      loadBaseDatos({ page: basePedidosPage, pageSize: basePedidosPageSize });
    } catch (err) {
      console.error('Error al actualizar UUID:', err);
      alert(err.message || 'No se pudieron actualizar los UUID.');
    } finally {
      setUuidIsUploading(false);
    }
  }, [uuidExcelData, uuidIsUploading, loadBaseDatos, basePedidosPage, basePedidosPageSize]);

  const onPagosSelectionChanged = useCallback(() => {
    if (pagosGridRef.current) {
      const selectedRows = pagosGridRef.current.api.getSelectedRows();
      setPagosSelectedCount(selectedRows.length);
    }
  }, []);

  const handlePagosFileUpload = useCallback((event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const binary = evt.target?.result;
      if (!binary) return;
      const workbook = XLSX.read(binary, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) return;
      const worksheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
      const mappedRows = rawRows
        .map((row) => {
          const normalized = {};
          Object.entries(row).forEach(([key, value]) => {
            const normalizedField = PAGOS_FIELD_LOOKUP.get(normalizeHeaderKey(key));
            if (normalizedField) {
              normalized[normalizedField] = value;
            }
          });
          let hasValue = false;
          const sanitized = {};
          PAGOS_FIELD_ORDER.forEach((field) => {
            const rawValue = normalized[field];
            if (rawValue !== null && typeof rawValue !== 'undefined' && rawValue !== '') {
              hasValue = true;
            }
            sanitized[field] = rawValue ?? '';
          });
          return hasValue ? sanitized : null;
        })
        .filter(Boolean);
      setPagosExcelData(mappedRows);
      setPagosPuedeCargar(mappedRows.length > 0);
      if (pagosFileInputRef.current) {
        pagosFileInputRef.current.value = '';
      }
    };
    reader.readAsBinaryString(file);
  }, []);

  const handleCargarPagosExcel = useCallback(async () => {
    if (!pagosExcelData.length || pagosIsUploading) return;
    const totalToUpload = pagosExcelData.length;
    setPagosIsUploading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/pagos/insertar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pagosExcelData)
      });
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.mensaje || 'No se pudo cargar la información de pagos.');
      }
      setPagosExcelData([]);
      setPagosPuedeCargar(false);
      loadPagos();
      alert(`Se cargaron ${payload.inserted || totalToUpload} registros de pagos.`);
    } catch (err) {
      alert(err.message || 'No se pudo cargar la información de pagos.');
    } finally {
      setPagosIsUploading(false);
    }
  }, [pagosExcelData, pagosIsUploading, loadPagos]);

  const handleDeletePagos = useCallback(async () => {
    if (!esSupervisor) return;
    if (!pagosGridRef.current) return;
    const selectedRows = pagosGridRef.current.api.getSelectedRows();
    if (!selectedRows.length) {
      alert('Selecciona al menos un renglón para borrar.');
      return;
    }
    const ids = selectedRows
      .map((row) => Number(row?.id))
      .filter((value) => Number.isInteger(value));
    if (!ids.length) {
      alert('No se pudieron identificar los registros seleccionados.');
      return;
    }
    const confirmed = window.confirm('¿Seguro que deseas borrar los pagos seleccionados?');
    if (!confirmed) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/pagos/borrar`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.mensaje || 'No se pudieron borrar los registros.');
      }
      loadPagos();
      setPagosSelectedCount(0);
      pagosGridRef.current.api.deselectAll();
    } catch (err) {
      alert(err.message || 'No se pudieron borrar los registros seleccionados.');
    }
  }, [esSupervisor, loadPagos]);

  const facturacionColumnDefs = useMemo(() => FACTURACION_COLUMNS.map((column) => ({
    ...column,
    hide: column.field && column.field !== '__select__' ? columnVisibility[column.field] === false : column.hide,
    width: column.field && column.field !== '__select__' && columnWidths[column.field]
      ? columnWidths[column.field]
      : column.width
  })), [columnVisibility, columnWidths]);

  const getFacturarRowId = useCallback((params) => {
    if (!params?.data) return String(params?.rowIndex ?? '');
    if (params.data.key != null) return String(params.data.key);
    if (params.data.pedido) return String(params.data.pedido);
    return String(params?.rowIndex ?? '');
  }, []);

  const facturarFrameworkComponents = useMemo(() => ({
    FacturarStatusRenderer,
    FacturarNumeroRenderer,
    FacturarEstatusPagoRenderer,
    FacturarUuidRenderer
  }), []);

  const facturarAgGridColumnDefs = useMemo(() => {
    const columns = [
      {
        headerName: '',
        field: '__select__',
        width: 46,
        minWidth: 46,
        maxWidth: 46,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        pinned: 'left',
        lockPinned: true,
        suppressMenu: true,
        sortable: false,
        filter: false,
        resizable: false
      },
      {
        headerName: 'Aseguradora',
        field: 'aseguradora',
        minWidth: 230,
        valueFormatter: ({ value }) => value || 'Sin aseguradora'
      },
      {
        headerName: 'Fecha pedido',
        field: 'fechaPedido',
        minWidth: 140,
        valueFormatter: ({ value }) => formatISODateForDisplay(value) || 'Sin fecha'
      },
      {
        headerName: 'Pedido',
        field: 'pedido',
        minWidth: 110,
        cellClass: 'facturar-bold-cell'
      },
      {
        headerName: 'Filas',
        field: 'totalRegistros',
        minWidth: 90,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: ({ value }) => Number(value || 0).toLocaleString('es-MX')
      },
      {
        headerName: 'Siniestro',
        field: 'siniestro',
        minWidth: 160
      },
      {
        headerName: 'OC',
        field: 'oc',
        minWidth: 150,
        valueFormatter: ({ value }) => value || '—'
      },
      {
        headerName: 'Estatus',
        field: 'pedidoStatus',
        minWidth: 140,
        cellRenderer: 'FacturarStatusRenderer'
      },
      {
        headerName: 'Número de factura',
        field: 'numeroFactura',
        minWidth: 160,
        cellRenderer: 'FacturarNumeroRenderer'
      },
      {
        headerName: 'Estatus de pago',
        field: 'estatusPago',
        minWidth: 150,
        cellRenderer: 'FacturarEstatusPagoRenderer'
      },
      {
        headerName: 'UUID',
        field: 'uuid',
        minWidth: 190,
        cellRenderer: 'FacturarUuidRenderer'
      },
      {
        headerName: 'Precio MXN',
        field: 'totalPrecio',
        minWidth: 130,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: ({ value }) => formatCurrency(value)
      },
      {
        headerName: 'IVA 16%',
        field: 'ivaCalculado',
        minWidth: 120,
        cellClass: 'ag-right-aligned-cell',
        valueGetter: ({ data }) => {
          const total = toNumber(data?.totalPrecio);
          if (Number.isNaN(total)) return '';
          return total * 0.16;
        },
        valueFormatter: ({ value }) => (value === '' ? '' : formatCurrency(value))
      },
      {
        headerName: 'Total',
        field: 'totalConIva',
        minWidth: 130,
        cellClass: 'ag-right-aligned-cell',
        valueGetter: ({ data }) => {
          const total = toNumber(data?.totalPrecio);
          if (Number.isNaN(total)) return '';
          return total * 1.16;
        },
        valueFormatter: ({ value }) => (value === '' ? '' : formatCurrency(value))
      }
    ];
    return columns.map((column) => {
      if (!column.field || column.field === '__select__') return column;
      const storedWidth = facturarColumnWidths[column.field];
      if (typeof storedWidth === 'number' && storedWidth > 0) {
        return { ...column, width: storedWidth };
      }
      return column;
    });
  }, [facturarColumnWidths]);

  const facturarAgGridDefaultColDef = useMemo(() => ({
    resizable: true,
    sortable: false,
    filter: false,
    suppressMenu: true,
    minWidth: 80
  }), []);

  const isFacturarRowSaving = useCallback((pedidoKey) => Boolean(pedidoFacturacionSaving[pedidoKey]), [pedidoFacturacionSaving]);

  const facturarGridContext = useMemo(() => ({
    onNumeroFacturaChange: handlePedidoNumeroFacturaChange,
    onNumeroFacturaBlur: handlePedidoNumeroFacturaBlur,
    onEstatusPagoChange: handlePedidoEstatusPagoChange,
    onUuidChange: handlePedidoUuidChange,
    onUuidBlur: handlePedidoUuidBlur,
    onToggleRows: handleTogglePedidoRows,
    isRowSaving: isFacturarRowSaving,
    expandedPedidoKey,
    getStatusMeta: getPedidoStatusMeta
  }), [
    handlePedidoNumeroFacturaBlur,
    handlePedidoNumeroFacturaChange,
    handlePedidoEstatusPagoChange,
    handlePedidoUuidBlur,
    handlePedidoUuidChange,
    handleTogglePedidoRows,
    isFacturarRowSaving,
    expandedPedidoKey
  ]);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 20 }}>
        <h2 style={{ marginTop: 0 }}>Facturación</h2>
        <p style={{ marginTop: 0, color: '#6b7280' }}>
          Vista con el mismo formato de la base de datos: búsqueda libre, carga desde Excel, selección múltiple y tabla con Ag Grid.
        </p>

        <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setActiveInnerTab('facturar')}
            style={{
              padding: '6px 16px',
              borderRadius: 10,
              border: activeInnerTab === 'facturar' ? '1px solid #6366f1' : '1px solid #d0d5dd',
              background: activeInnerTab === 'facturar' ? '#eef2ff' : '#f8fafc',
              color: '#1f2937',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Facturar
          </button>
          <button
            type="button"
            onClick={() => setActiveInnerTab('pagos')}
            style={{
              padding: '6px 16px',
              borderRadius: 10,
              border: activeInnerTab === 'pagos' ? '1px solid #6366f1' : '1px solid #d0d5dd',
              background: activeInnerTab === 'pagos' ? '#eef2ff' : '#f8fafc',
              color: '#1f2937',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Pagos
          </button>
        </div>

        <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, background: '#f8fafc', padding: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>Carga masiva</div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <div
              style={{
                flex: '1 1 320px',
                minWidth: 260,
                padding: 10,
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                background: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                gap: 8
              }}
            >
              <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 13 }}>Carga masiva de números de factura</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  ref={facturasFileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFacturasFileUpload}
                />
                <button
                  type="button"
                  onClick={() => facturasFileInputRef.current?.click()}
                  style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: '#ffffff', color: '#0f172a', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >
                  Seleccionar Excel
                </button>
                <button
                  type="button"
                  onClick={handleCargarFacturasExcel}
                  disabled={!facturasPuedeCargar || facturasIsUploading}
                  style={{ ...facturasUploadButtonStyle, padding: '4px 10px', fontSize: 12 }}
                >
                  {facturasIsUploading ? 'Actualizando...' : 'Actualizar'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#64748b', minHeight: 16 }}>
                {facturasExcelData.length > 0 && !facturasIsUploading && (
                  <span style={{ color: '#0f766e', fontWeight: 600 }}>
                    {facturasExcelData.length} pedidos listos
                  </span>
                )}
                {facturasIsUploading && (
                  <span style={{ color: '#2563eb', fontWeight: 600 }} role="status">
                    Procesando cambios...
                  </span>
                )}
                {!facturasExcelData.length && !facturasIsUploading && (
                  <span>Sube pedido + número de factura.</span>
                )}
              </div>
            </div>

            <div
              style={{
                flex: '1 1 320px',
                minWidth: 260,
                padding: 10,
                borderRadius: 12,
                border: '1px solid #f4d4d7',
                background: '#fffafa',
                display: 'flex',
                flexDirection: 'column',
                gap: 8
              }}
            >
              <div style={{ fontWeight: 600, color: '#7f1d1d', fontSize: 13 }}>Carga masiva de UUID por número de factura</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  ref={uuidFileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleUuidFileUpload}
                />
                <button
                  type="button"
                  onClick={() => uuidFileInputRef.current?.click()}
                  style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #f4c6cf', background: '#ffffff', color: '#7f1d1d', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >
                  Seleccionar Excel
                </button>
                <button
                  type="button"
                  onClick={handleCargarUuidExcel}
                  disabled={!uuidPuedeCargar || uuidIsUploading}
                  style={{ ...uuidUploadButtonStyle, padding: '4px 10px', fontSize: 12 }}
                >
                  {uuidIsUploading ? 'Aplicando...' : 'Actualizar'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#7f1d1d', minHeight: 16 }}>
                {uuidExcelData.length > 0 && !uuidIsUploading && (
                  <span style={{ color: '#9f1239', fontWeight: 600 }}>
                    {uuidExcelData.length} facturas listas
                  </span>
                )}
                {uuidIsUploading && (
                  <span style={{ color: '#9f1239', fontWeight: 600 }} role="status">
                    Actualizando UUID...
                  </span>
                )}
                {!uuidExcelData.length && !uuidIsUploading && (
                  <span>Archivo con "Número de factura" y "UUID".</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {activeInnerTab === 'listado' && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
              <div style={{ minWidth: 180, background: '#f5f7ff', borderRadius: 12, padding: '12px 16px' }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', color: '#4f46e5', letterSpacing: '0.05em' }}>Registros</div>
                <div style={{ fontWeight: 700, fontSize: 22 }}>{filteredRows.length.toLocaleString('es-MX')}</div>
              </div>
              <div style={{ minWidth: 180, background: '#ecfdf5', borderRadius: 12, padding: '12px 16px' }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', color: '#047857', letterSpacing: '0.05em' }}>Total unidades</div>
                <div style={{ fontWeight: 700, fontSize: 22 }}>{totalUnidades.toLocaleString('es-MX')}</div>
              </div>
              <div style={{ minWidth: 180, background: '#fff7ed', borderRadius: 12, padding: '12px 16px' }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', color: '#ea580c', letterSpacing: '0.05em' }}>Pendientes</div>
                <div style={{ fontWeight: 700, fontSize: 22 }}>{totalPendientes.toLocaleString('es-MX')}</div>
              </div>
              <div style={{ minWidth: 220, background: '#fef3c7', borderRadius: 12, padding: '12px 16px' }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', color: '#a16207', letterSpacing: '0.05em' }}>Total MXN</div>
                <div style={{ fontWeight: 700, fontSize: 22 }}>{formatCurrency(totalImporte)}</div>
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Buscar folio, razón social, referencia..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ padding: 8, minWidth: 260, borderRadius: 8, border: '1px solid #d0d5dd' }}
              />
              <button
                type="button"
                onClick={loadFacturacion}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#f3f4f6', cursor: 'pointer', fontWeight: 600 }}
              >
                Recargar datos
              </button>
              <button
                type="button"
                onClick={() => setShowColumnList((prev) => !prev)}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: showColumnList ? '#e0e7ff' : '#f3f4f6', cursor: 'pointer', fontWeight: 600 }}
              >
                {showColumnList ? 'Ocultar columnas' : 'Seleccionar columnas'}
              </button>
              <button
                type="button"
                onClick={handleDeleteSelected}
                disabled={selectedCount === 0 || isDeleting}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: selectedCount > 0 && !isDeleting ? '#dc2626' : '#fca5a5',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: selectedCount > 0 && !isDeleting ? 'pointer' : 'not-allowed'
                }}
              >
                {isDeleting ? 'Borrando...' : `Borrar seleccionados (${selectedCount})`}
              </button>
              <div style={{ fontWeight: 600, color: '#2563eb' }}>Filas mostradas: {filteredRows.length.toLocaleString('es-MX')}</div>
            </div>

            {showColumnList && (
              <div style={{ marginBottom: 16, padding: 12, borderRadius: 12, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', flexWrap: 'wrap', gap: 12, maxHeight: 220, overflowY: 'auto' }}>
                {FACTURACION_COLUMNS.filter((col) => col.field && col.field !== '__select__').map((column) => (
                  <label key={column.field} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 200 }}>
                    <input
                      type="checkbox"
                      checked={columnVisibility[column.field] !== false}
                      onChange={() => handleToggleColumn(column.field)}
                    />
                    <span>{column.headerName}</span>
                  </label>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'center' }}>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#f3f4f6', cursor: 'pointer', fontWeight: 600 }}
              >
                Seleccionar archivo Excel
              </button>
              <button
                type="button"
                onClick={handleUploadExcel}
                disabled={!excelPreview.length || isUploading}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: excelPreview.length && !isUploading ? '#2563eb' : '#93c5fd',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: excelPreview.length && !isUploading ? 'pointer' : 'not-allowed'
                }}
              >
                {isUploading ? 'Cargando...' : `Cargar ${excelPreview.length || 0} registros`}
              </button>
              {excelPreview.length > 0 && (
                <span style={{ color: '#047857', fontWeight: 600 }}>{excelPreview.length} filas listas para cargar</span>
              )}
            </div>

            {errorMessage && (
              <div style={{ marginBottom: 16, padding: 12, borderRadius: 12, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
                {errorMessage}
              </div>
            )}

            {loading ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Cargando registros...</div>
            ) : (
              <div className="ag-theme-alpine" style={{ height: 420, width: '100%', borderRadius: 12 }}>
                <AgGridReact
                  ref={gridRef}
                  columnDefs={facturacionColumnDefs}
                  rowData={filteredRows}
                  rowSelection="multiple"
                  getRowId={(params) => getRowIdentifier(params?.data) || ''}
                  suppressRowClickSelection={true}
                  suppressRowDeselection={true}
                  domLayout="normal"
                  suppressHorizontalScroll={false}
                  suppressMovableColumns={true}
                  enableBrowserTooltips={true}
                  enableCellTextSelection={true}
                  defaultColDef={{
                    resizable: true,
                    sortable: false,
                    filter: false,
                    minWidth: 70,
                    editable: false,
                  }}
                  headerHeight={28}
                  rowHeight={24}
                  onSelectionChanged={handleSelectionChanged}
                  onColumnResized={handleColumnResized}
                  onCellValueChanged={handleCellValueChanged}
                  onGridReady={handleGridReady}
                />
              </div>
            )}
          </>
        )}

        {activeInnerTab === 'facturar' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: '#f8fafc', borderRadius: 12, padding: 12, border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>
                    Adjuntos ({hasFacturacionRegistroSeleccionado ? `${facturacionAdjuntos.length}/${ADJUNTOS_MAX_PER_REGISTRO}` : '—'})
                  </h3>
                  <p style={{ margin: 0, color: '#6b7280', fontSize: 12 }}>
                    Selecciona un renglón de la tabla de Facturación para consultar, ver o subir documentos.
                    Espacios disponibles: {hasFacturacionRegistroSeleccionado ? facturacionAdjuntosSlotsDisponibles : 'Selecciona un registro'}.
                  </p>
                  {facturarSelectedRows.length > 1 && (
                    <p style={{ margin: '4px 0 0', color: '#92400e', fontSize: 12, fontWeight: 600 }}>
                      Hay {facturarSelectedRows.length} renglones seleccionados; se muestran los adjuntos del primer registro seleccionado.
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    type="file"
                    multiple
                    ref={facturacionAdjuntosFileInputRef}
                    style={{ display: 'none' }}
                    accept={ADJUNTOS_ACCEPTED_EXTENSIONS}
                    onChange={handleFacturacionAdjuntosFileChange}
                  />

                  <button
                    type="button"
                    onClick={handleFacturacionAdjuntosReload}
                    disabled={facturacionAdjuntosLoading || !hasFacturacionRegistroSeleccionado}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: '1px solid #bfdbfe',
                      background: facturacionAdjuntosLoading ? '#f3f4f6' : '#e0f2fe',
                      color: '#0f172a',
                      fontWeight: 600,
                      cursor: facturacionAdjuntosLoading || !hasFacturacionRegistroSeleccionado ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {facturacionAdjuntosLoading ? 'Actualizando...' : 'Actualizar lista'}
                  </button>

                  <button
                    type="button"
                    onClick={handleFacturacionAdjuntosUploadClick}
                    disabled={!hasFacturacionRegistroSeleccionado || facturacionAdjuntosUploading || facturacionAdjuntosSlotsDisponibles <= 0}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: '1px solid #bfdbfe',
                      background: !hasFacturacionRegistroSeleccionado || facturacionAdjuntosUploading || facturacionAdjuntosSlotsDisponibles <= 0 ? '#f3f4f6' : '#dbeafe',
                      color: '#1d4ed8',
                      fontWeight: 700,
                      cursor: !hasFacturacionRegistroSeleccionado || facturacionAdjuntosUploading || facturacionAdjuntosSlotsDisponibles <= 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {facturacionAdjuntosUploading ? 'Subiendo...' : 'Agregar archivos'}
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {!hasFacturacionRegistroSeleccionado ? (
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    Selecciona un registro de la tabla para consultar sus archivos adjuntos.
                  </div>
                ) : facturacionAdjuntosError ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#b42318' }}>{facturacionAdjuntosError}</span>
                    <button
                      type="button"
                      onClick={handleFacturacionAdjuntosReload}
                      style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff1f2', color: '#b42318', fontWeight: 600 }}
                    >
                      Reintentar
                    </button>
                  </div>
                ) : facturacionAdjuntosLoading ? (
                  <div style={{ fontSize: 13, color: '#475467' }}>Cargando adjuntos...</div>
                ) : facturacionAdjuntos.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#6b7280' }}>No hay archivos adjuntos para este registro.</div>
                ) : (
                  facturacionAdjuntos.map((archivo) => (
                    <div
                      key={archivo.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        alignItems: 'center',
                        padding: 10,
                        borderRadius: 10,
                        background: '#ffffff',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {archivo.file_name || `Adjunto ${archivo.id}`}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          {archivo.file_size ? `${Math.round(Number(archivo.file_size) / 1024)} KB` : 'Tamaño no disponible'}
                          {archivo.uploaded_by ? ` · ${archivo.uploaded_by}` : ''}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <a
                          href={`${API_BASE_URL}/api/basedatos/adjuntos/${archivo.id}/view`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ padding: '6px 10px', borderRadius: 8, background: '#eef2ff', color: '#3730a3', fontWeight: 700, textDecoration: 'none' }}
                        >
                          Ver
                        </a>
                        <a
                          href={`${API_BASE_URL}/api/basedatos/adjuntos/${archivo.id}/download`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ padding: '6px 10px', borderRadius: 8, background: '#dbeafe', color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}
                        >
                          Descargar
                        </a>
                        <button
                          type="button"
                          onClick={() => handleFacturacionAdjuntoDelete(archivo)}
                          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff1f2', color: '#b91c1c', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div style={{ background: '#ffffff', borderRadius: 12, padding: 12, border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <div>
                  <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>Pedidos de la base principal</h3>
                  <p style={{ margin: 0, color: '#6b7280' }}>
                    Se agrupan las {basePedidosSummary.totalDocumentos.toLocaleString('es-MX')} filas actuales de la pestaña Base de datos usando la columna PEDIDO.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => loadBaseDatos({ page: basePedidosPage, pageSize: basePedidosPageSize })}
                    disabled={isBasePedidosBusy}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 10,
                      border: '1px solid #d0d5dd',
                      background: isBasePedidosBusy ? '#e5e7eb' : '#f3f4f6',
                      fontWeight: 600,
                      cursor: isBasePedidosBusy ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isBasePedidosBusy ? 'Actualizando...' : 'Recargar base principal'}
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ flex: '1 1 160px', background: '#eef2ff', borderRadius: 10, padding: '8px 12px' }}>
                  <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#4338ca' }}>Pedidos únicos</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{basePedidosSummary.totalPedidos.toLocaleString('es-MX')}</div>
                </div>
                <div style={{ flex: '1 1 160px', background: '#ecfdf5', borderRadius: 10, padding: '8px 12px' }}>
                  <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#047857' }}>Filas totales</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{basePedidosSummary.totalDocumentos.toLocaleString('es-MX')}</div>
                </div>
                <div style={{ flex: '1 1 200px', background: '#fff7ed', borderRadius: 10, padding: '8px 12px' }}>
                  <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#c2410c' }}>Costo acumulado</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{formatCurrency(basePedidosSummary.totalCosto)}</div>
                </div>
                <div style={{ flex: '1 1 200px', background: '#fef3c7', borderRadius: 10, padding: '8px 12px' }}>
                  <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#a16207' }}>Precio acumulado</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{formatCurrency(basePedidosSummary.totalPrecio)}</div>
                </div>
              </div>
            </div>
            <div style={{ background: '#f8fafc', borderRadius: 12, padding: 12, border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>Filtrar por fecha de pedido</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: '#475569' }}>
                  Desde
                  <input
                    type="date"
                    value={basePedidosFilterDraft.fechaInicio}
                    onChange={(event) => handleDateFilterInputChange('fechaInicio', event?.target?.value || '')}
                    style={{ marginTop: 4, padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5f5', minWidth: 160 }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: '#475569' }}>
                  Hasta
                  <input
                    type="date"
                    value={basePedidosFilterDraft.fechaFin}
                    onChange={(event) => handleDateFilterInputChange('fechaFin', event?.target?.value || '')}
                    style={{ marginTop: 4, padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5f5', minWidth: 160 }}
                  />
                </label>
                <button
                  type="button"
                  onClick={handleApplyPedidoDateFilter}
                  disabled={dateFilterApplyDisabled}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 10,
                    border: 'none',
                    background: dateFilterApplyDisabled ? '#dbeafe' : '#2563eb',
                    color: dateFilterApplyDisabled ? '#94a3b8' : '#ffffff',
                    fontWeight: 600,
                    cursor: dateFilterApplyDisabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  Aplicar filtro
                </button>
                {showClearDateFilterButton && (
                  <button
                    type="button"
                    onClick={handleClearPedidoDateFilter}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 10,
                      border: '1px solid #94a3b8',
                      background: '#ffffff',
                      color: '#0f172a',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Limpiar
                  </button>
                )}
              </div>
              {isDateRangeInvalid && (
                <div style={{ fontSize: 12, color: '#b91c1c' }}>
                  La fecha inicial debe ser menor o igual a la fecha final.
                </div>
              )}
              {hasActiveDateFilter && dateFilterSummaryLabel && (
                <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{dateFilterSummaryLabel}</div>
              )}
              <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>Filtrar por aseguradora</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {ASEGURADORA_FILTERS.map((option) => {
                    const isActive = aseguradoraFilter === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleAseguradoraFilterChange(option.value)}
                        style={{
                          padding: '7px 18px',
                          borderRadius: 999,
                          border: `1px solid ${option.border}`,
                          background: isActive ? option.activeBg : '#ffffff',
                          color: isActive ? option.activeColor : option.textColor,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: 13,
                          boxShadow: isActive ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none'
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>Filtrar por estatus</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {PEDIDO_STATUS_FILTERS.map((option) => {
                    const isActive = statusFilter === option.value;
                    const meta = option.value === 'all' ? null : getPedidoStatusMeta(option.value);
                    const baseStyles = meta?.styles || { background: '#ffffff', color: '#1e293b', border: '#cbd5f5' };
                    const activeBackground = meta ? baseStyles.background : '#eef2ff';
                    const buttonStyles = {
                      padding: '6px 14px',
                      borderRadius: 999,
                      border: `1px solid ${isActive ? baseStyles.border : '#d0d5dd'}`,
                      background: isActive ? activeBackground : '#ffffff',
                      color: isActive ? baseStyles.color : '#475569',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: 13,
                      boxShadow: isActive ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none'
                    };
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleStatusFilterChange(option.value)}
                        style={buttonStyles}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {basePedidosError && (
              <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 12, padding: 14, color: '#b91c1c' }}>
                {basePedidosError}
              </div>
            )}
            {basePedidosLoading ? (
              <div style={{ padding: 20, borderRadius: 12, border: '1px dashed #d1d5db', textAlign: 'center', color: '#6b7280', background: '#f9fafb' }}>
                Cargando la base de datos principal...
              </div>
            ) : (
              <div style={{ background: '#ffffff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 0 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #e5e7eb', gap: 10 }}>
                  <div style={{ fontSize: 13, color: '#4b5563' }}>{basePedidosRangeLabel}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4b5563' }}>
                      Filas por página:
                      <select
                        value={basePedidosPageSize}
                        onChange={handlePedidosPageSizeChange}
                        disabled={isBasePedidosBusy}
                        style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid #d1d5db', background: '#ffffff', fontSize: 13 }}
                      >
                        {PEDIDOS_PAGE_SIZE_OPTIONS.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={handlePedidosPrevPage}
                      disabled={!canGoPrevPedidosPage || isBasePedidosBusy}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 8,
                        border: '1px solid #d0d5dd',
                        background: !canGoPrevPedidosPage || isBasePedidosBusy ? '#f3f4f6' : '#e0e7ff',
                        color: '#111827',
                        fontWeight: 600,
                        cursor: !canGoPrevPedidosPage || isBasePedidosBusy ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Anterior
                    </button>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', minWidth: 120, textAlign: 'center' }}>
                      Página {basePedidosPage.toLocaleString('es-MX')} de {basePedidosTotalPages.toLocaleString('es-MX')}
                    </div>
                    <button
                      type="button"
                      onClick={handlePedidosNextPage}
                      disabled={!canGoNextPedidosPage || isBasePedidosBusy}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 8,
                        border: '1px solid #d0d5dd',
                        background: !canGoNextPedidosPage || isBasePedidosBusy ? '#f3f4f6' : '#e0e7ff',
                        color: '#111827',
                        fontWeight: 600,
                        cursor: !canGoNextPedidosPage || isBasePedidosBusy ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
                {basePedidosRefreshing && (
                  <div style={{
                    padding: '10px 16px',
                    borderBottom: '1px solid #e5e7eb',
                    background: '#f8fafc',
                    color: '#475569',
                    fontSize: 13,
                    fontWeight: 600
                  }}>
                    Buscando nuevos resultados...
                  </div>
                )}
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {baseGroupedPedidos.length > 0 && (
                    <div style={{ border: '1px solid #cbd5f5', borderRadius: 14, background: '#ffffff', padding: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 10 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 700 }}>Detalle del pedido</div>
                          {expandedPedidoGroup ? (
                            <>
                              <div style={{ fontSize: 13, color: '#475569', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                                <span style={{ fontWeight: 600, color: '#0f172a' }}>Pedido {expandedPedidoGroup.pedido || 'sin folio'}</span>
                                <span style={{ color: '#1d4ed8' }}>· {expandedPedidoSiniestro || 'Sin siniestro registrado'}</span>
                                <span style={{ color: '#0f172a' }}>· OC {expandedPedidoOcLabel}</span>
                              </div>
                              {expandedPedidoTotals && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                  {[
                                    { label: 'Precio MXN', value: expandedPedidoTotals.subtotalFormatted },
                                    { label: 'IVA 16%', value: expandedPedidoTotals.ivaFormatted },
                                    { label: 'Total', value: expandedPedidoTotals.totalFormatted }
                                  ].map((summary) => (
                                    <div
                                      key={summary.label}
                                      style={{
                                        padding: '5px 10px',
                                        borderRadius: 8,
                                        border: '1px solid #e2e8f0',
                                        background: '#f8fafc',
                                        minWidth: 140
                                      }}
                                    >
                                      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>{summary.label}</div>
                                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{summary.value}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ fontSize: 13, color: '#475569' }}>
                              Haz clic en “Ver filas” en la tabla inferior para consultar las filas del pedido seleccionado.
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {expandedPedidoGroup && expandedPedidoError && (
                            <button
                              type="button"
                              onClick={handleRetryPedidoDetails}
                              disabled={expandedPedidoLoading}
                              style={{
                                padding: '6px 12px',
                                borderRadius: 10,
                                border: '1px solid #f97316',
                                background: expandedPedidoLoading ? '#fed7aa' : '#fff7ed',
                                color: '#c2410c',
                                fontWeight: 600,
                                cursor: expandedPedidoLoading ? 'not-allowed' : 'pointer'
                              }}
                            >
                              Reintentar
                            </button>
                          )}
                          {expandedPedidoGroup && (
                            <button
                              type="button"
                              onClick={() => handleTogglePedidoRows(expandedPedidoGroup.key, { forceClose: true })}
                              style={{
                                padding: '6px 12px',
                                borderRadius: 10,
                                border: '1px solid #cbd5f5',
                                background: '#f8fafc',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              Cerrar detalle
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ position: 'relative' }}>
                        <div
                          style={{
                            overflowX: 'auto',
                            overflowY: expandedPedidoNeedsScroll ? 'auto' : 'hidden',
                            maxHeight: `${FACTURAR_DETAIL_TABLE_MAX_HEIGHT}px`,
                            minHeight: `${FACTURAR_DETAIL_TABLE_MAX_HEIGHT}px`
                          }}
                        >
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1040 }}>
                            <thead>
                              <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                                <th style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, borderBottom: '1px solid #e5e7eb' }}>Pedido</th>
                                <th style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, borderBottom: '1px solid #e5e7eb' }}>Siniestro</th>
                                <th style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, borderBottom: '1px solid #e5e7eb' }}>OC</th>
                                <th style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, borderBottom: '1px solid #e5e7eb' }}>Item</th>
                                <th style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, borderBottom: '1px solid #e5e7eb' }}>Nuevo estatus</th>
                                <th style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, borderBottom: '1px solid #e5e7eb' }}>Número de factura</th>
                                <th style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, borderBottom: '1px solid #e5e7eb' }}>Estatus de pago</th>
                                <th style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, borderBottom: '1px solid #e5e7eb' }}>UUID</th>
                                <th style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>Precio</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandedPedidoRows.map((row, index) => {
                                const nuevoEstatus = normalizePedidoValue(row?.NUEVO_ESTATUS);
                                const normalizedStatus = nuevoEstatus ? nuevoEstatus.trim().toUpperCase() : '';
                                const hideFacturacion = normalizedStatus && NUEVO_ESTATUS_EXCLUSION.includes(normalizedStatus);
                                const numeroFactura = hideFacturacion ? '—' : (normalizePedidoValue(row?.['NUMERO DE FACTURA']) || '—');
                                const estatusPago = hideFacturacion ? '—' : (normalizePedidoValue(row?.ESTATUS_DE_PAGO) || '—');
                                const uuid = hideFacturacion ? '—' : (normalizePedidoValue(row?.UUID) || '—');
                                const ocValue = normalizePedidoValue(row?.OC) || '—';
                                return (
                                  <tr key={`${expandedPedidoGroup?.key || 'sin-pedido'}-${index}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE }}>{normalizeBasePedido(row?.PEDIDO) || 'Sin pedido'}</td>
                                    <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE }}>{normalizePedidoValue(row?.SINIESTRO) || '—'}</td>
                                    <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE }}>{ocValue}</td>
                                    <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE }}>{normalizePedidoValue(row?.ITEM) || '—'}</td>
                                    <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE }}>{nuevoEstatus || '—'}</td>
                                    <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE }}>{numeroFactura}</td>
                                    <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE }}>{estatusPago}</td>
                                    <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, fontFamily: 'monospace', fontSize: 11 }}>{uuid}</td>
                                    <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, textAlign: 'right' }}>{formatCurrency(row?.PRECIO)}</td>
                                  </tr>
                                );
                              })}
                              {expandedPedidoFillerRows > 0 && Array.from({ length: expandedPedidoFillerRows }).map((_, fillerIndex) => (
                                <tr
                                  key={`filler-${expandedPedidoGroup?.key || 'none'}-${fillerIndex}`}
                                  style={{ borderBottom: '1px solid #f8fafc', background: '#fff' }}
                                >
                                  <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, color: '#cbd5f5' }}>—</td>
                                  <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, color: '#cbd5f5' }}>—</td>
                                  <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, color: '#cbd5f5' }}>—</td>
                                  <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, color: '#cbd5f5' }}>—</td>
                                  <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, color: '#cbd5f5' }}>—</td>
                                  <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, color: '#cbd5f5' }}>—</td>
                                  <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, color: '#cbd5f5' }}>—</td>
                                  <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, color: '#cbd5f5' }}>—</td>
                                  <td style={{ ...FACTURAR_DETAIL_CELL_BASE_STYLE, color: '#cbd5f5', textAlign: 'right' }}>—</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {detailOverlayState && (
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: detailOverlayState.type === 'error' ? 'rgba(254, 242, 242, 0.95)' : 'rgba(255, 255, 255, 0.92)',
                              color: detailOverlayState.type === 'error' ? '#b91c1c' : '#475569',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textAlign: 'center',
                              padding: 16,
                              fontWeight: 600,
                              borderRadius: 10
                            }}
                          >
                            {detailOverlayState.message}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                      <input
                        type="text"
                        value={facturarSearchText}
                        onChange={(event) => setFacturarSearchText(event.target.value)}
                        placeholder="Buscar por pedido, siniestro u OC"
                        style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #d0d5dd', minWidth: 240 }}
                      />
                      {facturarSearchText && (
                        <button
                          type="button"
                          onClick={() => setFacturarSearchText('')}
                          style={{
                            padding: '5px 10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5f5',
                            background: '#f1f5f9',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          Limpiar filtro
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleExportFacturarSelection}
                        disabled={!facturarHasSelection}
                        style={{
                          padding: '5px 10px',
                          borderRadius: 8,
                          border: `1px solid ${facturarHasSelection ? '#0284c7' : '#cbd5f5'}`,
                          background: facturarHasSelection ? '#0ea5e9' : '#e0f2fe',
                          color: facturarHasSelection ? '#ffffff' : '#94a3b8',
                          fontWeight: 600,
                          cursor: facturarHasSelection ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Exportar selección (Excel)
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                        Mostrando {facturarFilteredPedidos.length.toLocaleString('es-MX')} pedidos
                      </div>
                      {facturarHasSelection && (
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#0369a1' }}>
                          {facturarSelectionCount.toLocaleString('es-MX')} pedidos seleccionados
                        </div>
                      )}
                    </div>
                  </div>
                  {facturarFilteredPedidos.length === 0 ? (
                    <div style={{
                      padding: 24,
                      borderRadius: 12,
                      border: '1px dashed #cbd5f5',
                      background: '#f8fafc',
                      textAlign: 'center',
                      color: '#475569',
                      fontWeight: 600
                    }}>
                      {facturarEmptyStateMessage}
                    </div>
                  ) : (
                    <div className="ag-theme-alpine facturar-grid" style={{ width: '100%', height: 420, borderRadius: 12 }}>
                      <AgGridReact
                        ref={facturarGridRef}
                        rowData={facturarFilteredPedidos}
                        columnDefs={facturarAgGridColumnDefs}
                        defaultColDef={facturarAgGridDefaultColDef}
                        rowSelection="multiple"
                        rowHeight={32}
                        headerHeight={32}
                        suppressHorizontalScroll={false}
                        suppressMovableColumns={true}
                        suppressRowClickSelection={true}
                        enableCellTextSelection={true}
                        domLayout="normal"
                        components={facturarFrameworkComponents}
                        context={facturarGridContext}
                        getRowId={getFacturarRowId}
                        getRowClass={(params) => {
                          const paymentStatus = (params?.data?.estatusPago || '')
                            .toString()
                            .trim()
                            .toUpperCase();
                          if (paymentStatus === 'PAGADO') {
                            return 'facturar-row--pagado';
                          }
                          if (paymentStatus === 'ERROR EN COSTO') {
                            return 'facturar-row--error-costo';
                          }
                          if (paymentStatus === 'NO HAY ORDEN EN SIE') {
                            return 'facturar-row--no-orden-sie';
                          }
                          if (paymentStatus === 'CANCELADO') {
                            return 'facturar-row--cancelado';
                          }
                          if (params?.data?.pedidoStatus === 'facturar') {
                            return 'facturar-row--facturar';
                          }
                          if (normalizePedidoStatus(params?.data?.pedidoStatus) === 'cancelado') {
                            return 'facturar-row--cancelado';
                          }
                          if (normalizePedidoStatus(params?.data?.pedidoStatus) === 'pendiente de recepcion') {
                            return 'facturar-row--pendiente-recepcion';
                          }
                          if (params?.data?.pedidoStatus === FACTURADO_PEDIDO_STATUS) {
                            return 'facturar-row--facturado';
                          }
                          return undefined;
                        }}
                        onGridReady={handleFacturarGridReady}
                        onColumnResized={handleFacturarColumnResized}
                        onRowClicked={handleFacturarRowClicked}
                        onSelectionChanged={handleFacturarSelectionChanged}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeInnerTab === 'pagos' && (
          <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Pagos</h3>
            <p style={{ marginTop: 0, color: '#6b7280' }}>
              Carga y administra pagos con su mismo formato de importación.
            </p>
            <div style={{ marginBottom: 10 }}>
              {esSupervisor && (
                <button
                  type="button"
                  onClick={handleDeletePagos}
                  style={{ background: '#dc2626', color: '#fff', fontWeight: 'bold', padding: '6px 14px', borderRadius: 8, border: 'none' }}
                  disabled={pagosSelectedCount === 0 || pagosIsUploading}
                >
                  Borrar seleccionados
                </button>
              )}
              <span style={{ marginLeft: 16, fontWeight: 'bold', color: '#047857' }}>
                Seleccionadas: {pagosSelectedCount}
              </span>
              <span style={{ marginLeft: 16, fontWeight: 'bold', color: '#3730a3' }}>
                Total de filas: {filteredPagos.length}
              </span>
            </div>
            <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                ref={pagosFileInputRef}
                style={{ display: 'none' }}
                onChange={handlePagosFileUpload}
              />
              <button
                type="button"
                onClick={() => pagosFileInputRef.current?.click()}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#f3f4f6', cursor: 'pointer' }}
              >
                Seleccionar Excel
              </button>
              <button
                type="button"
                onClick={handleCargarPagosExcel}
                disabled={!pagosPuedeCargar || pagosIsUploading}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: pagosPuedeCargar && !pagosIsUploading ? '#2563eb' : '#93c5fd',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: pagosPuedeCargar && !pagosIsUploading ? 'pointer' : 'not-allowed'
                }}
              >
                {pagosIsUploading ? 'Cargando…' : 'Cargar información'}
              </button>
              {pagosExcelData.length > 0 && (
                <span style={{ color: '#0f766e', fontWeight: 600 }}>
                  {pagosExcelData.length} filas listas para cargar
                </span>
              )}
            </div>
            <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Buscar en pagos..."
                value={searchPagosText}
                onChange={(event) => setSearchPagosText(event.target.value)}
                style={{ padding: 6, minWidth: 240, borderRadius: 8, border: '1px solid #d0d5dd' }}
              />
              {pagosIsUploading && (
                <span style={{ color: '#2563eb', fontWeight: 600 }} role="status">
                  Guardando información…
                </span>
              )}
            </div>
            <div className="ag-theme-alpine" style={{ height: 450, width: '100%', fontSize: '13px', overflow: 'auto', borderRadius: 12 }}>
              <AgGridReact
                ref={pagosGridRef}
                columnDefs={PAGOS_COLUMNS}
                rowData={filteredPagos}
                rowSelection="multiple"
                getRowId={(params) => (params?.data?.id ? String(params.data.id) : undefined)}
                domLayout="normal"
                suppressHorizontalScroll={false}
                suppressMovableColumns={true}
                enableBrowserTooltips={true}
                enableCellTextSelection={true}
                defaultColDef={{
                  resizable: true,
                  sortable: false,
                  filter: false,
                  minWidth: 60,
                  editable: false,
                }}
                headerHeight={32}
                rowHeight={28}
                onSelectionChanged={onPagosSelectionChanged}
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FacturacionPage;
